import {
  navigate,
  navigateScreenAware,
  navigationRef,
  replace,
} from 'components/Navigation/navigationRef';
import { ModalScreens, Screens, Stacks } from 'components/Navigation/types';
import { ScreenSize } from 'lib/constants';
import { isError, isNetworkError } from 'lib/isError';
import {
  arrayJoin,
  getScreenSize,
  filterArrayEmpty,
  randomKey,
} from 'lib/riskbase';
import {
  apiAbort,
  apiGet,
  apiPost,
  apiPut,
  apiRemove,
  requestGet,
  requestPatch,
  requestPost,
  requestRoute,
} from 'service/api';
import * as http from 'service/api/constants';
import {
  CATCH_ERROR,
  REMOTE_ACCOUNT_LOAD_DETAILS,
  REMOTE_ACCOUNT_SAVE_AVATAR,
  UPDATE_USER_AVATAR,
  UPDATE_USER_EMAIL,
  UPDATE_USER_NAME,
  UPDATE_USER_OFA,
  UPDATE_USER_TFA,
  doUserOrganisationSet,
} from 'store/actions';
import {
  ALERT,
  ALERT_ERROR,
  doFilePreviewPdf,
  doFileShare,
  doToast,
  eventReorderScreenClose,
  eventReorderScreenDone,
  eventSelectScreenClose,
  eventSelectScreenSelection,
  eventTypeScrollToItem,
  HIDE_BLOCKING_LOADER,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  SET_GENERIC,
  SET_INCOMING_VALUE,
  SHOW_BLOCKING_LOADER,
} from 'store/app/actions';
import { DATA_SYNC, DO_MANUAL_SYNC } from 'store/sync/actions';
import { call, put, race, select, take, takeEvery } from 'typed-redux-saga';
import {
  AssessmentQuestionType,
  genericReportingVisualsSize,
} from 'types/types';
import {
  AccountEmailChangeGet,
  AccountEmailChangePost,
  AccountEmailChangePut,
  AccountPasswordChangeDelete,
  AccountPasswordChangeGet,
  AccountPasswordChangePut,
  AccountSaveDetails,
  GetDetails,
  GetTfa,
  PostTfa,
  RemoveTfa,
} from 'types/Endpoints/Account';
import {
  EPAssessmentSetupActionCreate,
  EPAssessmentSetupActionDelete,
  EPAssessmentSetupActionUpdate,
  EPAssessmentSetupAnswerCreate,
  EPAssessmentSetupAnswerDelete,
  EPAssessmentSetupAnswerUpdate,
  EPAssessmentSetupChecklistItemCreate,
  EPAssessmentSetupChecklistItemDelete,
  EPAssessmentSetupChecklistItemUpdate,
  EPAssessmentSetupControlCreate,
  EPAssessmentSetupControlDelete,
  EPAssessmentSetupControlUpdate,
  EPAssessmentSetupFolderCreate,
  EPAssessmentSetupFolderDelete,
  EPAssessmentSetupFolderUpdate,
  EPAssessmentSetupGetLibrary,
  EPAssessmentSetupTemplateCreate,
  EPAssessmentSetupTemplateData,
  EPAssessmentSetupTemplateUpdate,
  EPAssessmentSetupQuestionCreate,
  EPAssessmentSetupQuestionDelete,
  EPAssessmentSetupQuestionUpdate,
  EPAssessmentSetupSectionCreate,
  EPAssessmentSetupSectionDelete,
  EPAssessmentSetupSectionUpdate,
  EPAssessmentSetupTemplateDelete,
  EPAssessmentSetupFoldersOrder,
  EPAssessmentSetupSectionsOrder,
  EPAssessmentSetupQuestionsOrder,
  EPSettingsAssessmentChecks,
  EPSettingsAssessmentCheckUpdate,
  EPAssessmentSetupCreateFlag,
  EPAssessmentSetupUpdateFlag,
  EPAssessmentSetupDeleteFlag,
  EPAssessmentSetupCreateSignatory,
  EPAssessmentSetupUpdateSignatory,
  EPAssessmentSetupDeleteSignatory,
  EPAssessmentSetupOrderSignatories,
} from 'types/Endpoints/AssessmentSettings';
import {
  EPFieldSettingsFieldCreate,
  EPFieldSettingsFieldDelete,
  EPFieldSettingsFieldUpdate,
  EPFieldSettingsGet,
  EPFieldSettingsGroupAddGroup,
  EPFieldSettingsGroupCreate,
  EPFieldSettingsGroupDelete,
  EPFieldSettingsGroupUpdate,
  EPFieldSettingsResultCount,
  EPFieldSettingsSectionCreate,
  EPFieldSettingsSectionDelete,
  EPFieldSettingsSectionDeleteClient,
  EPFieldSettingsSectionLinkClients,
  EPFieldSettingsSectionUnlinkClient,
  EPFieldSettingsSectionUpdate,
  EPFieldSettingsUpdate,
} from 'types/Endpoints/FieldSettings';

import { sagaConfirm } from 'store/sagaHelpers';
import { last, orderBy, union } from 'lodash';
import { pluralise } from 'lib/pluralise';
import { ICONS } from 'components/Icon';
import { theme } from 'lib/theme';
import {
  EPBillingAdminOrganisationSave,
  EPBillingData,
  EPBillingDiscountCreate,
  EPBillingDiscountDelete,
  EPBillingDiscountUpdate,
  EPBillingGet,
  EPBillingItemCreate,
  EPBillingItemDelete,
  EPBillingItemUpdate,
  EPBillingProductCreate,
  EPBillingProductDelete,
  EPBillingProductUnitsUpdate,
  EPBillingProductUpdate,
} from 'types/Endpoints/Billing';
import {
  EPReportsGetReport,
  EPReportsGetReportListData,
  EPReportsGetReports,
} from 'types/Endpoints/Reports';
import {
  EPApiCreateUser,
  EPApiCreateUserPreflight,
  EPApiCreateWebhook,
  EPApiCreateWebhookPreflight,
  EPApiDeleteUser,
  EPApiDeleteWebhook,
  EPApiGetData,
  EPApiTestWebhook,
  EPApiUpdateUser,
  EPApiUpdateWebhook,
} from 'types/Endpoints/Api';
import { smartText, str } from 'lib/str';
import {
  EPEngageAddDocuments,
  EPEngageAddElements,
  EPEngageGetData,
  EPEngageOrderDocuments,
  EPEngageOrderElements,
  EPEngageRemoveDocument,
  EPEngageRemoveElement,
  EPEngageUpdateDocument,
  EPEngageUpdateElement,
  EPEngageUpdateGeneral,
} from 'types/Endpoints/Engage';
import { EPSsoSettingsGetData, EPSsoSettingsUpdate } from 'types/Endpoints/Sso';
import {
  EPOrganisationSettingsData,
  EPOrganisationSettingsLogoDelete,
  EPOrganisationSettingsLogoSave,
  EPOrganisationSettingsSave,
} from 'types/Endpoints/Organisation';
import { selectUserSelectedOrganisation } from 'store/auth/selectors';
import {
  systemEventSetupOrganisationNotOpened,
  systemEventSetupOrganisationOpened,
} from 'store/ActionsEvents';
import { orderBy as naturalOrderBy } from 'natural-orderby';
import {
  getFileTypeFromUntrustedExtension,
  returnFileAsBase64,
} from 'service/file';
import { FileTypeGroupsEnum } from 'service/file/types';
import delay from 'delay';
import {
  REMOTE_ACCOUNT_EMAIL_CHANGE_CONFIRM,
  REMOTE_ACCOUNT_EMAIL_CHANGE_LOAD,
  REMOTE_ACCOUNT_EMAIL_CHANGE_REQUEST,
  REMOTE_ACCOUNT_LOAD_TFA,
  REMOTE_ACCOUNT_PASSWORD_CHANGE_LOAD,
  REMOTE_ACCOUNT_PASSWORD_CHANGE_SAVE,
  REMOTE_ACCOUNT_PASSWORD_DELETE,
  REMOTE_ACCOUNT_SAVE_DETAILS,
  REMOTE_ACCOUNT_SAVE_TFA,
  REMOTE_CREATE_ASSET,
  doFieldSettingsFieldDelete,
  doFieldSettingsGroupDelete,
  doFieldSettingsSectionDelete,
  doFieldSettingsDeleteCustomer,
  doFieldSettingsLinkCustomer,
  REMOTE_LOAD_CREATE_ASSET,
  doFieldSettingsLoad,
  REMOTE_ASESETUP_LOAD,
  doFieldSettingsFieldSave,
  doFieldSettingsGroupSave,
  doFieldSettingsSectionSave,
  REMOTE_ASESETUP_SET,
  doFieldSettingUnlinkCustomer,
  REMOTE_ASESETUP_QUESTIONSAVE,
  REMOTE_ASESETUPLIBRARY_QUESTIONOPEN,
  REMOTE_ASESETUP_CHECKLISTITEMOPEN,
  doRemoteSetOrganisation,
  REMOTE_ASESETUP_MERGE,
  REMOTE_ASESETUP_CHECKLISTITEMSAVE,
  REMOTE_ASESETUP_ACTIONDELETE,
  REMOTE_ASESETUP_REMOVE,
  REMOTE_ASESETUP_CONTROLDELETE,
  REMOTE_ASESETUP_CONTROLSAVE,
  REMOTE_ASESETUP_CONTROLOPEN,
  REMOTE_ASESETUP_ACTIONOPEN,
  REMOTE_ASESETUP_ACTIONSAVE,
  REMOTE_ASESETUP_CHECKLISTITEMDELETE,
  REMOTE_ASESETUP_QUESTIONDELETE,
  REMOTE_ASESETUP_ANSWEROPEN,
  REMOTE_ASESETUP_ANSWERSAVE,
  REMOTE_ASESETUP_ANSWERDELETE,
  REMOTE_ASESETUP_FOLDEROPEN,
  REMOTE_ASESETUP_FOLDERSAVE,
  REMOTE_ASESETUP_SECTIONOPEN,
  REMOTE_ASESETUP_SECTIONSAVE,
  REMOTE_ASESETUP_FOLDERDELETE,
  REMOTE_ASESETUP_SECTIONDELETE,
  doSetupAseLoad,
  doAseSetupTemplateOpen,
  doSetupAseSetData,
  doAseSetupTemplateSave,
  doSetupAseMergeData,
  doAseSetupTemplateDelete,
  doSetupAseRemoveData,
  REMOTE_ASESETUP_FOLDERSORDER,
  REMOTE_ASESETUP_SECTIONSORDER,
  REMOTE_ASESETUP_OPENSECTIONSORDER,
  REMOTE_ASESETUP_OPENQUESTIONSORDER,
  REMOTE_ASESETUP_QUESTIONSORDER,
  REMOTE_ASESETUP_OPENFOLDERSORDER,
  doFieldSettingsInit,
  doSetupBillingOpen,
  doSetupBillingSetData,
  doSetupBillingPdf,
  doSetupBillingCsv,
  doFieldSettingsSetSectionId,
  doFieldSettingsSetData,
  doFieldSettingsSectionSaved,
  doFieldSettingsSetGroupId,
  doFieldSettingsGroupSaved,
  doFieldSettingsSetFieldId,
  doFieldSettingsFieldSaved,
  doFieldSettingsGroupAddGroup,
  doFieldSettingsLoadResultCount,
  doFieldSettingsSetResultCount,
  doFieldSettingsSetLiteGroupId,
  doSettingsAseChecksLoad,
  doSettingsAseChecksSetData,
  doSettingsAseCheckOpen,
  doSettingsAseCheckSave,
  doSettingsAseChecksMergeData,
  doReportingGetReportListData,
  doReportingSelectReportOpen,
  doReportingSelectReport,
  doReportingSetReport,
  doReportingSetFilterValues,
  doReportingMergeDataListRaw,
  doReportingSetDataListLoading,
  doReportingGetReportCsv,
  doSetupApiOpen,
  doSetupApiCreateUserPreflight,
  doSetupApiCreateUser,
  doSetupApiSetData,
  doSetupApiUpdateUser,
  doSetupApiDeleteUser,
  doSetupApiCreateWebhookPreflight,
  doSetupApiDeleteWebhook,
  doSetupApiUpdateWebhook,
  doSetupApiCreateWebhook,
  doSetupApiTestWebhook,
  doSetupEngageOpen,
  doSetupEngageSetData,
  doSetupEngageDocumentUpdate,
  doSetupEngageAddElements,
  doSetupEngageOrderElements,
  doSetupEngageElementUpdate,
  doSetupEngageAddDocuments,
  doSetupEngageOrderDocuments,
  doSetupEngageRemoveDocument,
  doSetupEngageRemoveElement,
  doSetupEngageUpdateGeneral,
  doSetupBillingAdminUpdateProduct,
  doSetupBillingAdminDeleteProduct,
  doSetupBillingAdminUpdateItem,
  doSetupBillingAdminDeleteItem,
  doSetupBillingAdminUpdateProductUnits,
  doSetupBillingAdminDeleteProductUnits,
  doSetupBillingDraftCsv,
  doSetupBillingDraftPdf,
  doSetupBillingAdminOpen,
  doSetupBillingAdminSetData,
  doReportingSetDataVisualRaw,
  doReportingMergeDraftFilterValues,
  doReportingShowReportFilterModal,
  doReportingAddPage,
  doReportingSetReportVisualsSize,
  doSetupSsoOpen,
  doSetupSsoSetData,
  doSetupSsoUpdate,
  doSetupOrganisationOpen,
  doSetupOrganisationSetData,
  doSetupOrganisationOnBehalfOfOpen,
  doSetupOrganisationUpdateData,
  doSetupOrganisationSave,
  doAseSetupFlagSave,
  doAseSetupFlagDelete,
  doSetupBillingAdminUpdateDiscount,
  doSetupBillingAdminDeleteDiscount,
  doSetupOrganisationSaveLogo,
  doFieldSettingsReorderSections,
  doAseSetupSignatorySave,
  doAseSetupSignatoryDelete,
  doAseSetupSignatoriesOrder,
  doSetupBillingAdminOrganisationSave,
  doSetupBillingCreditNote,
  doSetupBillingStatementPdf,
  doSetupBillingStatementCsv,
  doReportingLoad,
  doSettingsComplianceAndAssessmentTypesLoad,
  doSettingsComplianceAndAssessmentTypesSetData,
  doCreateComplianceType,
  doSettingsComplianceTypeUpdate,
  doUpdateComplianceType,
  doCreateAssessmentType,
  doSettingsAssessmentTypeUpdate,
  doUpdateAssessmentType,
  doSettingsSeveritiesOpen,
  doSettingsSeveritiesLoad,
  doSettingsSeveritiesSetData,
  doSettingsSeveritiesReorder,
  doSettingsSeveritiesUpdate,
  doSettingsSeveritiesSet,
  doSettingsSeveritiesCreate,
  doSettingsSeveritiesSharedWithOrganisationsSetData,
  doSettingsSeveritiesDelete,
  doSettingsSeveritiesRemove,
  doSettingsSeveritiesManagingOrganisationSetData,
  doSettingsIntegrationsLoad,
  doSettingsIntegrationsSetData,
  doSettingsIntegrationSet,
  doSettingsIntegrationCreate,
  doSettingsIntegrationUpdate,
  doSettingsIntegrationRemove,
  doSettingsIntegrationRemoved,
} from './actions';
import {
  selectAseSetupChecklistItemHasActions,
  selectAseSetupChecklistItemHasControls,
  selectAseSetupLibraryAnswer,
  selectAseSetupLibraryAnswerHasQuestions,
  selectAseSetupLibraryAnswerSubItems,
  selectAseSetupLibraryFolder,
  selectAseSetupLibraryFolderHasSections,
  selectAseSetupLibraryFolders,
  selectAseSetupLibraryFolderSubItems,
  selectAseSetupLibraryOrganisationHasFolders,
  selectAseSetupLibraryQuestion,
  selectAseSetupLibraryQuestions,
  selectAseSetupLibraryQuestionSubItems,
  selectAseSetupLibrarySection,
  selectAseSetupLibrarySectionHasQuestions,
  selectAseSetupLibrarySections,
  selectAseSetupLibrarySectionSubItems,
  selectBillingDiscount,
  selectBillingItem,
  selectBillingProduct,
  selectEngageDocuments,
  selectEngageElements,
  selectFieldSettingsGroupId,
  selectOrganisationFieldSettings,
  selectRemoteCustomers,
  selectRemoteOrganisationId,
  selectRptReportBasics,
  selectRptReportDraftFilterValues,
  selectRptReportFilterValues,
  selectRptReportPageNumber,
  selectRptReportPages,
  selectRptReportType,
  selectSetupOrganisation,
} from './selectors';
import {
  APIResponseCreateAsset,
  APIResponseLoadCreateAsset,
  PageTypeEnum,
} from './types';
import {
  SettingsGetComplianceAndAssessmentTypes,
  SettingsPatchAssessmentType,
  SettingsPatchComplianceType,
  SettingsPostAssessmentType,
  SettingsPostComplianceType,
} from 'types/DTOs/OrganisationSettingDTOs';
import { appApiRoutes } from '@riskbase/types/http/appApi';

function* loadCreateAsset(
  action: ReturnType<typeof REMOTE_LOAD_CREATE_ASSET>,
): Generator {
  try {
    yield* put(SHOW_BLOCKING_LOADER());
    const response = yield* apiGet<APIResponseLoadCreateAsset>(
      http.CREATE_ASSET,
    );
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.CREATEASSET,
        params: {
          organisations: response.organisations,
        },
      }),
    );
    yield* put(HIDE_BLOCKING_LOADER());
  } catch (error) {
    yield* put(HIDE_BLOCKING_LOADER());
    if (isError(error)) {
      yield* put(ALERT_ERROR({ action, error }));
    }
  }
}

function* createAsset(
  action: ReturnType<typeof REMOTE_CREATE_ASSET>,
): Generator {
  try {
    yield* put(SHOW_BLOCKING_LOADER());
    const response = yield* apiPost<APIResponseCreateAsset>(
      http.CREATE_ASSET,
      action.payload,
    );
    // Download the new asset
    yield* put(DO_MANUAL_SYNC({ addAssetIds: [response.assetId] }));
    // Wait for data sync to finish
    yield* take([DATA_SYNC.success, DATA_SYNC.failure]);
    // Pop the create asset page away
    yield* put(NAVIGATE_MODAL_BACK());
    yield* put(HIDE_BLOCKING_LOADER());
  } catch (error) {
    yield* put(HIDE_BLOCKING_LOADER());
    if (isError(error)) {
      yield* put(ALERT_ERROR({ action, error }));
    }
  }
}

export const remoteSideEffects = [
  takeEvery(REMOTE_LOAD_CREATE_ASSET, loadCreateAsset),
  takeEvery(REMOTE_CREATE_ASSET, createAsset),

  // Field Settings
  takeEvery(doFieldSettingsLoad.request, function* f(action) {
    const organisationId = action.payload.id;
    const screenSize = getScreenSize();
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiGet<EPFieldSettingsGet['response']>(
        http.API_ORGANISATION_SETTINGS_FIELDS(organisationId),
      );
      yield* put(doFieldSettingsInit());
      yield* apiAbort('doFieldSettingsLoadResultCount');
      yield* put(doRemoteSetOrganisation(organisationId));
      yield* put(doFieldSettingsSetResultCount(undefined));
      yield* put(doFieldSettingsSetData(response));
      if (screenSize.size === ScreenSize.small) {
        navigate(Stacks.SETTINGS, Screens.SETTINGS_FIELDS, undefined);
      } else {
        replace(Stacks.SETTINGS, Screens.SETTINGS_FIELDS, undefined);
      }
      yield* put(doFieldSettingsLoadResultCount());
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  takeEvery(doFieldSettingsLoadResultCount, function* f() {
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    try {
      yield* put(
        doFieldSettingsSetResultCount(
          (yield* apiGet<EPFieldSettingsResultCount['response']>(
            http.API_ORGANISATION_SETTINGS_FIELDS_RESULT_COUNT(organisationId),
            {
              abortKey: 'doFieldSettingsLoadResultCount',
            },
          )).fields,
        ),
      );
    } catch (error) {
      if (!isNetworkError(error)) {
        yield* put(CATCH_ERROR({ error }));
      }
    }
  }),
  takeEvery(doFieldSettingsReorderSections, function* f(action) {
    const fieldSettings = yield* select(selectOrganisationFieldSettings);
    const eventKey = randomKey();
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.REORDER,
        params: {
          title: 'Reorder Sections',
          options: fieldSettings.sectionIds.reduce<
            { value: string; name: string }[]
          >((acc, sectionId) => {
            const section = fieldSettings.sections[sectionId];
            if (section) {
              acc.push({
                value: section.id,
                name: section.name,
              });
            }
            return acc;
          }, []),
          eventKey,
          closeOnDone: false,
        },
      }),
    );
    let runAgain: boolean = true;
    do {
      const yielded = yield* race({
        cancel: take(eventReorderScreenClose),
        done: take(eventReorderScreenDone),
      });
      if (
        (yielded.cancel && yielded.cancel?.payload.key !== eventKey) ||
        (yielded.done && yielded.done?.payload.key !== eventKey)
      ) {
        // wrong eventKey - run 'do' again
      } else if (yielded.cancel) {
        runAgain = false;
      } else {
        const organisationId = (yield* select(selectRemoteOrganisationId))!;
        yield* put(SHOW_BLOCKING_LOADER());
        try {
          const response = yield* apiPut<EPFieldSettingsUpdate['response']>(
            http.API_ORGANISATION_SETTINGS_FIELDS(organisationId),
            {
              sectionIds: yielded.done?.payload.stringValues || [],
            },
          );
          yield* put(doFieldSettingsSetData(response));
          yield* put(NAVIGATE_MODAL_BACK([ModalScreens.REORDER]));
          runAgain = false;
        } catch (error) {
          if (isError(error)) {
            yield* put(ALERT_ERROR({ action, error }));
          }
        }
        yield* put(HIDE_BLOCKING_LOADER());
      }
    } while (runAgain);
  }),
  takeEvery(doFieldSettingsSectionSave, function* f(action) {
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    let sectionId: string;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      if (action.payload.id) {
        const response = yield* apiPut<
          EPFieldSettingsSectionUpdate['response']
        >(
          http.API_ORGANISATION_SETTINGS_FIELDS_SECTION(
            organisationId,
            action.payload.id,
          ),
          action.payload.data,
        );
        yield* put(doFieldSettingsSetData(response));
        sectionId = action.payload.id;
      } else {
        const response = yield* apiPost<
          EPFieldSettingsSectionCreate['response']
        >(
          http.API_ORGANISATION_SETTINGS_FIELDS_SECTION(organisationId),
          action.payload.data,
        );
        yield* put(doFieldSettingsSetData(response));
        yield* put(doFieldSettingsSetSectionId(response.newId));
        sectionId = response.newId;
      }
      yield* put(HIDE_BLOCKING_LOADER());
      yield* put(doFieldSettingsSectionSaved(sectionId));
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(doFieldSettingsSectionDelete, function* f(action) {
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<EPFieldSettingsSectionDelete['response']>(
        http.API_ORGANISATION_SETTINGS_FIELDS_SECTION(
          organisationId,
          action.payload.id,
          true,
        ),
      );
      yield* put(
        doFieldSettingsLoad.success({
          fieldSettings: response,
        }),
      );
      yield* put(HIDE_BLOCKING_LOADER());
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(doFieldSettingsDeleteCustomer, function* f(action) {
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPFieldSettingsSectionDeleteClient['response']
      >(
        http.API_ORGANISATION_SETTINGS_FIELDS_CLIENT(
          organisationId,
          action.payload.id,
          action.payload.clientId,
          'delete',
        ),
      );
      yield* put(
        doFieldSettingsLoad.success({
          fieldSettings: response,
        }),
      );
      yield* put(HIDE_BLOCKING_LOADER());
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(doFieldSettingUnlinkCustomer, function* f(action) {
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPFieldSettingsSectionUnlinkClient['response']
      >(
        http.API_ORGANISATION_SETTINGS_FIELDS_CLIENT(
          organisationId,
          action.payload.id,
          action.payload.clientId,
          'unlink',
        ),
      );
      yield* put(
        doFieldSettingsLoad.success({
          fieldSettings: response,
        }),
      );
      yield* put(HIDE_BLOCKING_LOADER());
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(doFieldSettingsLinkCustomer, function* f(action) {
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const payload: EPFieldSettingsSectionLinkClients['post'] = {
        clientIds: action.payload.clientIds,
      };
      const response = yield* apiPost<
        EPFieldSettingsSectionLinkClients['response']
      >(
        http.API_ORGANISATION_SETTINGS_FIELDS_CLIENT(
          organisationId,
          action.payload.id,
        ),
        payload,
      );
      yield* put(
        doFieldSettingsLoad.success({
          fieldSettings: response,
        }),
      );
      yield* put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK());
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(doFieldSettingsGroupSave, function* f(action) {
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    yield* put(SHOW_BLOCKING_LOADER());
    let groupId: string;
    try {
      if (action.payload.id) {
        const response = yield* apiPut<EPFieldSettingsGroupUpdate['response']>(
          http.API_ORGANISATION_SETTINGS_FIELDS_GROUP(
            organisationId,
            action.payload.id,
          ),
          action.payload.data,
        );
        yield* put(doFieldSettingsSetData(response));
        groupId = action.payload.id;
      } else {
        const openGroupId = yield* select(selectFieldSettingsGroupId);
        const response = yield* apiPost<EPFieldSettingsGroupCreate['response']>(
          http.API_ORGANISATION_SETTINGS_FIELDS_GROUP(organisationId),
          {
            ...action.payload.data,
            parentGroupId: action.payload.isLite ? openGroupId : null,
          },
        );
        yield* put(doFieldSettingsSetData(response));
        if (action.payload.isLite) {
          yield* put(doFieldSettingsSetLiteGroupId(response.newId));
        } else {
          yield* put(doFieldSettingsSetGroupId(response.newId));
        }
        groupId = response.newId;
      }
      yield* put(HIDE_BLOCKING_LOADER());
      yield* put(
        doFieldSettingsGroupSaved({
          id: groupId,
          isLite: action.payload.isLite,
        }),
      );
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(doFieldSettingsGroupAddGroup, function* f(action) {
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPFieldSettingsGroupAddGroup['response'],
        EPFieldSettingsGroupAddGroup['post']
      >(
        http.API_ORGANISATION_SETTINGS_FIELDS_GROUP_ADD_GROUP(
          organisationId,
          action.payload.id,
        ),
        {
          groupId: action.payload.childGroupId,
        },
      );
      yield* put(doFieldSettingsSetData(response));
      yield* put(HIDE_BLOCKING_LOADER());
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(doFieldSettingsGroupDelete, function* f(action) {
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      yield* put(
        doFieldSettingsLoad.success({
          fieldSettings: yield* apiPost<EPFieldSettingsGroupDelete['response']>(
            http.API_ORGANISATION_SETTINGS_FIELDS_GROUP(
              organisationId,
              action.payload.id,
              true,
            ),
          ),
        }),
      );
      yield* put(HIDE_BLOCKING_LOADER());
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(doFieldSettingsFieldSave, function* f(action) {
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    let fieldId: string;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      if (action.payload.id !== undefined) {
        yield* put(
          doFieldSettingsSetData(
            yield* apiPut<EPFieldSettingsFieldUpdate['response']>(
              http.API_ORGANISATION_SETTINGS_FIELDS_FIELD(
                organisationId,
                action.payload.id,
              ),
              action.payload.data,
            ),
          ),
        );
        fieldId = action.payload.id;
      } else {
        const response = yield* apiPost<EPFieldSettingsFieldCreate['response']>(
          http.API_ORGANISATION_SETTINGS_FIELDS_FIELD(organisationId),
          action.payload.data,
        );
        yield* put(doFieldSettingsSetData(response));
        yield* put(doFieldSettingsSetFieldId(response.newId));
        fieldId = response.newId;
      }
      yield* put(HIDE_BLOCKING_LOADER());
      yield* put(doFieldSettingsFieldSaved(fieldId));
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(doFieldSettingsFieldDelete, function* f(action) {
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      yield* put(
        doFieldSettingsLoad.success({
          fieldSettings: yield* apiPost<EPFieldSettingsFieldDelete['response']>(
            http.API_ORGANISATION_SETTINGS_FIELDS_FIELD(
              organisationId,
              action.payload.id,
              true,
            ),
          ),
        }),
      );
      yield* put(HIDE_BLOCKING_LOADER());
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  // Account Details
  takeEvery(REMOTE_ACCOUNT_LOAD_DETAILS, function* f(action): Generator {
    yield put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiGet<GetDetails['response']>(
        http.API_ACCOUNT_DETAILS(),
      );
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(UPDATE_USER_NAME(response.name));
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.SETTINGSPROFILEDETAILS,
          params: response,
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(REMOTE_ACCOUNT_SAVE_DETAILS, function* f(action) {
    yield put(SHOW_BLOCKING_LOADER());
    try {
      const post: AccountSaveDetails['post'] = {
        forename: action.payload.forename,
        surname: action.payload.surname,
        phone: action.payload.phone,
        postNominals: action.payload.postNominals,
        qualifications: action.payload.qualifications,
      };
      const response = yield* apiPut<AccountSaveDetails['response']>(
        http.API_ACCOUNT_DETAILS(),
        post,
      );
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(UPDATE_USER_NAME(response.name));
      yield* put(NAVIGATE_MODAL_BACK());
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(REMOTE_ACCOUNT_SAVE_AVATAR, function* f(action): Generator {
    yield put(SHOW_BLOCKING_LOADER());
    try {
      if (action.payload.image) {
        yield* put(
          UPDATE_USER_AVATAR(
            (yield* apiPost<
              {
                image: string;
              },
              string
            >(http.API_ACCOUNT_AVATAR(), action.payload.image)).image,
          ),
        );
      } else {
        yield* call(apiRemove, http.API_ACCOUNT_AVATAR());
        yield* put(UPDATE_USER_AVATAR(null));
      }
      yield put(HIDE_BLOCKING_LOADER());
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  // Account Email
  takeEvery(REMOTE_ACCOUNT_EMAIL_CHANGE_LOAD, function* f(action) {
    yield put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiGet<AccountEmailChangeGet['response']>(
        http.API_ACCOUNT_EMAIL_CHANGE(),
      );
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.SETTINGSPROFILEEMAIL,
          params: {
            features: response.features,
          },
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(REMOTE_ACCOUNT_EMAIL_CHANGE_REQUEST, function* f(action) {
    yield put(SHOW_BLOCKING_LOADER());
    try {
      const payload: AccountEmailChangePost['post'] = {
        email: action.payload.email,
      };
      const response = yield* apiPost<AccountEmailChangePost['response']>(
        http.API_ACCOUNT_EMAIL_CHANGE(),
        payload,
      );
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        SET_INCOMING_VALUE({
          key: 'emailChangeToken',
          value: { token: response.token, email: response.email },
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(REMOTE_ACCOUNT_EMAIL_CHANGE_CONFIRM, function* f(action) {
    yield put(SHOW_BLOCKING_LOADER());
    try {
      const payload: AccountEmailChangePut['post'] = {
        code: action.payload.code,
        token: action.payload.token,
      };
      const response = yield* apiPut<AccountEmailChangePut['response']>(
        http.API_ACCOUNT_EMAIL_CHANGE(),
        payload,
      );
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(ALERT({ text: 'Your email address has been updated' }));
      yield* put(UPDATE_USER_EMAIL(response.email));
      yield* put(NAVIGATE_MODAL_BACK());
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  // Account Password
  takeEvery(REMOTE_ACCOUNT_PASSWORD_CHANGE_LOAD, function* f(action) {
    yield put(SHOW_BLOCKING_LOADER());
    try {
      yield* apiGet<AccountPasswordChangeGet>(
        http.API_ACCOUNT_PASSWORD_CHANGE(),
      );
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.SETTINGSPROFILEPASSWORD,
          params: {},
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(REMOTE_ACCOUNT_PASSWORD_CHANGE_SAVE, function* f(action) {
    yield put(SHOW_BLOCKING_LOADER());
    try {
      const payload: AccountPasswordChangePut['post'] = {
        newPassword: action.payload.newPassword,
        oldPassword: action.payload.oldPassword,
      };
      yield* call(apiPut, http.API_ACCOUNT_PASSWORD_CHANGE(), payload);
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(ALERT({ text: 'Your password has been updated' }));
      yield* put(NAVIGATE_MODAL_BACK());
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(REMOTE_ACCOUNT_PASSWORD_DELETE, function* f(action) {
    yield put(SHOW_BLOCKING_LOADER());
    try {
      (yield* call(
        apiRemove,
        http.API_ACCOUNT_PASSWORD_CHANGE(),
      )) as AccountPasswordChangeDelete;
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(ALERT({ text: 'Your account is now passwordless!' }));
      yield* put(UPDATE_USER_OFA('passwordless'));
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  // Account TFA
  takeEvery(REMOTE_ACCOUNT_LOAD_TFA, function* f(action) {
    yield put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiGet<GetTfa['response']>(
        http.API_ACCOUNT_TFA(),
      );
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(UPDATE_USER_TFA(response.enabled));
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.SETTINGSPROFILETFA,
          params: response,
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  takeEvery(REMOTE_ACCOUNT_SAVE_TFA, function* f(action) {
    yield put(SHOW_BLOCKING_LOADER());
    let response: RemoveTfa['response'] | PostTfa['response'];
    try {
      if (!action.payload) {
        // Remove TFA
        response = (yield* call(
          apiRemove,
          http.API_ACCOUNT_TFA(),
        )) as RemoveTfa['response'];
      } else {
        // Verify TFA
        const post: PostTfa['post'] = {
          token: action.payload.token || '',
        };
        response = yield* apiPost<PostTfa['response']>(
          http.API_ACCOUNT_TFA(),
          post,
        );
      }
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(UPDATE_USER_TFA(response.enabled));
      yield* put(NAVIGATE_MODAL_BACK());
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doSettingsAseChecksLoad, function* f(action) {
    try {
      yield* put(SHOW_BLOCKING_LOADER());
      const response = yield* apiGet<EPSettingsAssessmentChecks['response']>(
        http.urlSettingsAssessmentChecks(action.payload.organisationId),
      );
      yield* put(doRemoteSetOrganisation(action.payload.organisationId));
      yield* put(doSettingsAseChecksSetData(response));
      yield put(HIDE_BLOCKING_LOADER());
      navigateScreenAware(
        Stacks.SETTINGS,
        Screens.SettingsAssessmentCompliance,
        undefined,
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doSettingsAseCheckOpen, function* f(action) {
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.SettingsAseCheck,
        params: {
          id: action.payload.id,
        },
      }),
    );
  }),

  takeEvery(doSettingsAseCheckSave, function* f(action) {
    const { id, assessmentTypeId } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      yield* put(
        doSettingsAseChecksMergeData(
          yield* apiPut<
            EPSettingsAssessmentCheckUpdate['response'],
            EPSettingsAssessmentCheckUpdate['post']
          >(http.urlSettingsAssessmentChecks(organisationId), {
            id,
            assessmentTypeId,
          }),
        ),
      );
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsAseCheck]));
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  /** Assessment Setup Template */

  takeEvery(doSetupAseLoad, function* f(action) {
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiGet<EPAssessmentSetupTemplateData['response']>(
        http.API_ASESETUP_TEMPLATES(action.payload.organisationId),
      );
      yield* put(doRemoteSetOrganisation(action.payload.organisationId));
      yield* put(doSetupAseSetData(response));
      yield put(HIDE_BLOCKING_LOADER());
      navigateScreenAware(
        Stacks.SETTINGS,
        Screens.SETTINGS_ASSESSMENTS,
        undefined,
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doAseSetupTemplateOpen, function* f(action) {
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.ASESETUPTEMPLATE,
        params: {
          newData: action.payload.newData,
        },
      }),
    );
  }),

  takeEvery(doAseSetupTemplateSave, function* f(action) {
    const { id, data, close } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    let templateId: number;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      // New
      let response:
        | EPAssessmentSetupTemplateCreate['response']
        | EPAssessmentSetupTemplateUpdate['response'];
      if (!id) {
        if (!data.complianceSubtypeId) {
          throw new Error('Missing compliance subtype ID');
        }
        // Create template
        response = yield* apiPost<
          EPAssessmentSetupTemplateCreate['response'],
          EPAssessmentSetupTemplateCreate['post']
        >(http.API_ASESETUP_TEMPLATE(organisationId), data);
        templateId = Object.values(response.templates)[0]!.id;
      } else {
        // Save template
        response = yield* apiPut<
          EPAssessmentSetupTemplateUpdate['response'],
          EPAssessmentSetupTemplateUpdate['post']
        >(http.API_ASESETUP_TEMPLATE(organisationId, id), data);
        templateId = id;
      }
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        doSetupAseMergeData({
          ...response,
          opened: {
            templateId,
          },
          templates: response.templates,
          library: {
            questions: response.library.questions,
          },
        }),
      );
      if (close) {
        yield* put(NAVIGATE_MODAL_BACK());
      }
      yield* put(doToast(id ? 'Template Updated' : 'Template Created'));
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doAseSetupTemplateDelete, function* f(action) {
    const { id } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    if (
      !(yield* call(sagaConfirm, {
        title: 'Delete Template',
        text: 'Existing assessments will not be affected',
        confirmText: 'Delete',
        destructive: true,
      }))
    ) {
      return;
    }
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPAssessmentSetupTemplateDelete['response']
      >(http.API_ASESETUP_TEMPLATE(organisationId, id, true));
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        doSetupAseMergeData({
          ...response,
          opened: {
            templateId: undefined,
          },
          library: {
            questions: response.library.questions,
          },
        }),
      );
      yield* put(
        doSetupAseRemoveData({
          templates: [id],
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doAseSetupSignatorySave, function* f(action) {
    const { signatory, isNew } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      let response:
        | EPAssessmentSetupCreateSignatory['response']
        | EPAssessmentSetupUpdateSignatory['response'];
      if (isNew) {
        response = yield* apiPost<
          EPAssessmentSetupCreateSignatory['response'],
          EPAssessmentSetupCreateSignatory['post']
        >(http.apiAseSetupSignatory(organisationId), {
          name: signatory.name,
          declaration: signatory.declaration,
          requireOnSubmit: signatory.requireOnSubmit,
          allowUnavailable: signatory.allowUnavailable,
        });
      } else {
        response = yield* apiPut<
          EPAssessmentSetupUpdateSignatory['response'],
          EPAssessmentSetupUpdateSignatory['post']
        >(http.apiAseSetupSignatory(organisationId, signatory.id), {
          name: signatory.name,
          declaration: signatory.declaration,
          requireOnSubmit: signatory.requireOnSubmit,
          allowUnavailable: signatory.allowUnavailable,
        });
      }
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        doSetupAseMergeData({
          signatories: response.signatories,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.AseSetupSignatory]));
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doAseSetupSignatoryDelete, function* f(action) {
    const { id, name } = action.payload;
    if (
      !(yield* sagaConfirm({
        title: 'Delete Signatory',
        subtitle: name,
        text: 'Deleting this signatory will remove it from assessment templates and any non-approved assessments',
        confirmText: 'Delete Signatory',
        destructive: true,
      }))
    ) {
      return;
    }
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPAssessmentSetupDeleteSignatory['response'],
        EPAssessmentSetupDeleteSignatory['post']
      >(http.apiAseSetupSignatory(organisationId, id, true));
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        doSetupAseMergeData({
          signatories: response.signatories,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.AseSetupSignatory]));
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doAseSetupSignatoriesOrder, function* f(action) {
    const { order } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPut<
        EPAssessmentSetupOrderSignatories['response'],
        EPAssessmentSetupOrderSignatories['post']
      >(http.apiAseSetupSignatoryOrder(organisationId), {
        ids: order,
      });
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.REORDER]));
      yield* put(
        doSetupAseMergeData({
          signatories: response.signatories,
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  // Flags

  takeEvery(doAseSetupFlagSave, function* f(action) {
    const { flag, isNew } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      // New
      let response:
        | EPAssessmentSetupCreateFlag['response']
        | EPAssessmentSetupUpdateFlag['response'];
      if (isNew) {
        // Create template
        response = yield* apiPost<
          EPAssessmentSetupCreateFlag['response'],
          EPAssessmentSetupCreateFlag['post']
        >(http.apiAseSetupFlag(organisationId), {
          name: flag.name,
          description: flag.description,
          setApproved: flag.setApproved,
          setInProgress: flag.setInProgress,
          setSubmitted: flag.setSubmitted,
          showOnReport: flag.showOnReport,
        });
      } else {
        // Save template
        response = yield* apiPut<
          EPAssessmentSetupUpdateFlag['response'],
          EPAssessmentSetupUpdateFlag['post']
        >(http.apiAseSetupFlag(organisationId, flag.id), {
          name: flag.name,
          description: flag.description,
          setApproved: flag.setApproved,
          setInProgress: flag.setInProgress,
          setSubmitted: flag.setSubmitted,
          showOnReport: flag.showOnReport,
        });
      }
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        doSetupAseMergeData({
          flags: response.flags,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.AseSetupFlag]));
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doAseSetupFlagDelete, function* f(action) {
    const { flagId, name } = action.payload;
    if (
      !(yield* sagaConfirm({
        title: 'Delete Flag',
        subtitle: name,
        text: 'Deleting this flag will remove it from assessment templates and any non-approved assessments',
        confirmText: 'Delete Flag',
        destructive: true,
      }))
    ) {
      return;
    }
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPAssessmentSetupDeleteFlag['response'],
        EPAssessmentSetupDeleteFlag['post']
      >(http.apiAseSetupFlag(organisationId, flagId, true));
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        doSetupAseMergeData({
          flags: response.flags,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.AseSetupFlag]));
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  /** Assessment Setup Library */

  takeEvery(REMOTE_ASESETUP_LOAD, function* f(action) {
    yield* put(SHOW_BLOCKING_LOADER());
    const screenSize = getScreenSize();
    try {
      const response = yield* apiGet<EPAssessmentSetupGetLibrary['response']>(
        http.API_ASESETUP_LIBRARY(action.payload.organisationId),
      );
      yield* put(doRemoteSetOrganisation(action.payload.organisationId));
      yield* put(REMOTE_ASESETUP_SET(response));
      yield put(HIDE_BLOCKING_LOADER());
      if (screenSize.size === ScreenSize.small) {
        navigate(Stacks.SETTINGS, Screens.SETTINGS_QUESTIONS, undefined);
      } else {
        replace(Stacks.SETTINGS, Screens.SETTINGS_QUESTIONS, undefined);
      }
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUPLIBRARY_QUESTIONOPEN, function* f(action) {
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.ASESETUPLIBRARYQUESTION,
        params: {
          type: action.payload.type || null,
          parentQuestionId: action.payload.parentQuestionId || null,
          sectionId: action.payload.sectionId || null,
        },
      }),
    );
  }),

  takeEvery(REMOTE_ASESETUP_ANSWERSAVE, function* f(action) {
    const { id, data, addData, close } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    let answerId: number;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      // New
      let response:
        | EPAssessmentSetupAnswerCreate['response']
        | EPAssessmentSetupAnswerUpdate['response'];
      if (!id) {
        if (!addData.questionId) {
          throw new Error('Missing question ID');
        }
        // Create checklist
        const payload: EPAssessmentSetupAnswerCreate['post'] = {
          questionId: addData.questionId,
          answer: data.answer,
          description: data.description,
          reportDescription: data.reportDescription,
          negative: data.negative,
          actionIds: data.actionIds,
        };
        response = yield* apiPost<EPAssessmentSetupAnswerCreate['response']>(
          http.API_ASESETUP_ANSWER(organisationId),
          payload,
        );
        answerId = Object.values(response.library.answers)[0]!.id;
      } else {
        // Save checklist
        const payload: EPAssessmentSetupAnswerUpdate['post'] = {
          answer: data.answer,
          description: data.description,
          reportDescription: data.reportDescription,
          negative: data.negative,
          actionIds: data.actionIds,
        };
        response = yield* apiPut<EPAssessmentSetupAnswerUpdate['response']>(
          http.API_ASESETUP_ANSWER(organisationId, id),
          payload,
        );
        answerId = id;
      }
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
          opened: {
            answerId,
          },
          library: response.library,
        }),
      );
      if (close) {
        yield* put(NAVIGATE_MODAL_BACK());
      }
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_QUESTIONSAVE, function* f(action) {
    const {
      id,
      data,
      controlIds,
      orderedChecklistItemIds: checklistItemsOrder,
      orderedAnswerIds: answersOrder,
      addData,
      close,
      linkedQuestionIds,
    } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    let questionId: number;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      // New
      let response:
        | EPAssessmentSetupQuestionCreate['response']
        | EPAssessmentSetupQuestionUpdate['response'];
      if (!id) {
        if (!addData.answerId && !addData.sectionId) {
          throw new Error('Missing section or answer ID');
        }
        // Create checklist
        const payload: EPAssessmentSetupQuestionCreate['post'] = {
          answerId: addData.answerId,
          sectionId: addData.sectionId,
          type: addData.type,
          question: data.question,
          description: data.description,
          reportDescription: data.reportDescription,
          controlIds,
        };
        response = yield* apiPost<EPAssessmentSetupQuestionCreate['response']>(
          http.API_ASESETUP_QUESTION(organisationId),
          payload,
        );
        questionId = Object.values(response.library.questions)[0]!.id;
      } else {
        // Save checklist
        const payload: EPAssessmentSetupQuestionUpdate['post'] = {
          question: data.question,
          description: data.description,
          reportDescription: data.reportDescription,
          controlIds,
          orderedChecklistItemIds: checklistItemsOrder,
          orderedAnswerIds: answersOrder,
          linkedQuestionIds,
        };
        response = yield* apiPut<EPAssessmentSetupQuestionUpdate['response']>(
          http.API_ASESETUP_QUESTION(organisationId, id),
          payload,
        );
        questionId = id;
      }
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
          opened: {
            questionId,
          },
          library: response.library,
        }),
      );
      if (close) {
        yield* put(NAVIGATE_MODAL_BACK());
      }
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_CHECKLISTITEMOPEN, function* f(action) {
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.ASESETUPLIBRARYCHECKLISTITEM,
        params: {
          questionId: action.payload.questionId || null,
        },
      }),
    );
  }),

  takeEvery(REMOTE_ASESETUP_ANSWEROPEN, function* f(action) {
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.ASESETUPLIBRARYANSWER,
        params: {
          questionId: action.payload.questionId || null,
        },
      }),
    );
  }),

  takeEvery(REMOTE_ASESETUP_CHECKLISTITEMSAVE, function* f(action) {
    const { id, data, addData, close } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    let checklistItemId: number;
    try {
      // New
      let response:
        | EPAssessmentSetupChecklistItemCreate['response']
        | EPAssessmentSetupChecklistItemUpdate['response'];
      if (!id) {
        if (!addData.questionId) {
          throw new Error('Missing question ID');
        }
        // Create checklist
        const payload: EPAssessmentSetupChecklistItemCreate['post'] = {
          questionId: addData.questionId,
          text: data.text,
          positiveActionIds: data.positiveActionIds,
          negativeActionIds: data.negativeActionIds,
          positiveControlIds: data.positiveControlIds,
          negativeControlIds: data.negativeControlIds,
        };
        response = yield* apiPost<
          EPAssessmentSetupChecklistItemCreate['response']
        >(http.API_ASESETUP_CHECKLISTITEM(organisationId), payload);
        checklistItemId = parseInt(
          Object.keys(response.library.checklistItems)[0]!,
          10,
        );
      } else {
        // Save checklist
        const payload: EPAssessmentSetupChecklistItemUpdate['post'] = {
          text: data.text,
          positiveActionIds: data.positiveActionIds,
          negativeActionIds: data.negativeActionIds,
          positiveControlIds: data.positiveControlIds,
          negativeControlIds: data.negativeControlIds,
        };
        response = yield* apiPut<
          EPAssessmentSetupChecklistItemUpdate['response']
        >(http.API_ASESETUP_CHECKLISTITEM(organisationId, id), payload);
        checklistItemId = id;
      }
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
          opened: {
            checklistItemId,
          },
          library: response.library,
        }),
      );
      if (close) {
        yield* put(NAVIGATE_MODAL_BACK());
      }
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_CHECKLISTITEMDELETE, function* f(action) {
    const { id } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    const actions = yield* select((state) =>
      selectAseSetupChecklistItemHasActions(state, id),
    );
    const controls = yield* select((state) =>
      selectAseSetupChecklistItemHasControls(state, id),
    );
    const totalActions = union(actions.negative, actions.positive).length;
    const totalControls = union(controls.negative, controls.positive).length;
    if (
      !(yield* call(sagaConfirm, {
        title: 'Delete Checklist Item',
        text: `The checklist item will still appear in existing assessments but not new ones${
          totalActions || totalControls
            ? `\n\nThe ${[
                pluralise(totalActions, 'action', 'actions', ''),
                pluralise(totalControls, 'control', 'controls', ''),
              ]
                .filter((a) => !!a)
                .join(' and ')} will be unlinked but not deleted; ${pluralise(
                totalActions + totalControls,
                "it'll",
                "they'll",
              )} remain available in the suggestions library`
            : ''
        }`,
        confirmText: 'Delete',
        destructive: true,
      }))
    ) {
      return;
    }
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPAssessmentSetupChecklistItemDelete['response']
      >(http.API_ASESETUP_CHECKLISTITEM(organisationId, id, true));
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
          suggestions: response.suggestions,
        }),
      );
      yield* put(
        REMOTE_ASESETUP_REMOVE({
          library: {
            checklistItems: [id],
          },
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_FOLDERDELETE, function* f(action) {
    const { id } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    const folder = yield* select((state) =>
      selectAseSetupLibraryFolder(state, id),
    );
    if (!folder) {
      return;
    }
    const subitems = yield* select((state) =>
      selectAseSetupLibraryFolderSubItems(state, id),
    );
    if (
      !(yield* call(sagaConfirm, {
        title: 'Delete Folder',
        text:
          'This will only affect new assessments; existing assessments will not be affected\n\n' +
          'All child items like sections, checklists, and questions will be deleted\n\n' +
          "Any actions and controls will be unlinked but not deleted; they'll remain available in the suggestions library",
        confirmText: 'Delete',
        destructive: true,
      }))
    ) {
      return;
    }
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPAssessmentSetupFolderDelete['response']
      >(http.API_ASESETUP_FOLDER(organisationId, id, true));
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
          suggestions: response.suggestions,
        }),
      );
      yield* put(
        REMOTE_ASESETUP_REMOVE({
          library: {
            folders: [id],
            sections: subitems.sectionIds,
            questions: [...subitems.questionIds, ...subitems.checklistIds],
            answers: subitems.answerIds,
            checklistItems: subitems.checklistItemIds,
          },
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_SECTIONDELETE, function* f(action) {
    const { id } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    const section = yield* select((state) =>
      selectAseSetupLibrarySection(state, id),
    );
    if (!section) {
      return;
    }
    const subitems = yield* select((state) =>
      selectAseSetupLibrarySectionSubItems(state, id),
    );
    if (
      !(yield* call(sagaConfirm, {
        title: 'Delete Section',
        text:
          'This will only affect new assessments; existing assessments will not be affected\n\n' +
          'All child items like checklists and questions will be deleted\n\n' +
          "Any actions and controls will be unlinked but not deleted; they'll remain available in the suggestions library",
        confirmText: 'Delete',
        destructive: true,
      }))
    ) {
      return;
    }
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPAssessmentSetupSectionDelete['response']
      >(http.API_ASESETUP_SECTION(organisationId, id, true));
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
          suggestions: response.suggestions,
        }),
      );
      yield* put(
        REMOTE_ASESETUP_REMOVE({
          library: {
            sections: [id],
            questions: [...subitems.questionIds, ...subitems.checklistIds],
            answers: subitems.answerIds,
            checklistItems: subitems.checklistItemIds,
          },
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_QUESTIONDELETE, function* f(action) {
    const { id } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';

    const question = yield* select((state) =>
      selectAseSetupLibraryQuestion(state, id),
    );
    if (!question) {
      return;
    }

    const subitems = yield* select((state) =>
      selectAseSetupLibraryQuestionSubItems(state, id),
    );

    const label =
      question.type === AssessmentQuestionType.checklist
        ? 'checklist'
        : 'question';
    const labelUppercase =
      question.type === AssessmentQuestionType.checklist
        ? 'Checklist'
        : 'Question';

    if (
      !(yield* call(sagaConfirm, {
        title: `Delete ${labelUppercase}`,
        text:
          `The ${label} will still appear in existing assessments but not new ones${
            subitems.checklistIds.length || subitems.questionIds.length
              ? `\n\nChild items will also be deleted (${arrayJoin(
                  [
                    pluralise(
                      subitems.checklistIds.length,
                      '1 checklists',
                      '{x} checklists',
                      '',
                    ),
                    pluralise(
                      subitems.questionIds.length,
                      '1 question',
                      '{x} questions',
                      '',
                    ),
                  ].filter(filterArrayEmpty),
                  ', ',
                  ' and ',
                )})`
              : ''
          }` +
          `${
            !!subitems.actionIds.length || !!subitems.controlIds.length
              ? `\n\nThe ${[
                  pluralise(subitems.actionIds.length, 'action', 'actions', ''),
                  pluralise(
                    subitems.controlIds.length,
                    'control',
                    'controls',
                    '',
                  ),
                ]
                  .filter(filterArrayEmpty)
                  .join(' and ')} will be unlinked but not deleted; ${pluralise(
                  subitems.actionIds.length + subitems.actionIds.length,
                  "it'll",
                  "they'll",
                )} remain available in the suggestions library`
              : ''
          }`,
        confirmText: 'Delete',
        destructive: true,
      }))
    ) {
      return;
    }
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPAssessmentSetupQuestionDelete['response']
      >(http.API_ASESETUP_QUESTION(organisationId, id, true));
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
          suggestions: response.suggestions,
        }),
      );
      yield* put(
        REMOTE_ASESETUP_REMOVE({
          library: {
            questions: [id, ...subitems.questionIds, ...subitems.checklistIds],
            answers: subitems.answerIds,
            checklistItems: subitems.checklistItemIds,
          },
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_ANSWERDELETE, function* f(action) {
    const { id } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';

    const answer = yield* select((state) =>
      selectAseSetupLibraryAnswer(state, id),
    );
    if (!answer) {
      return;
    }

    const subitems = yield* select((state) =>
      selectAseSetupLibraryAnswerSubItems(state, id),
    );

    if (
      !(yield* call(sagaConfirm, {
        title: 'Delete Answer',
        text:
          `The answer will still appear in existing assessments but not new ones${
            subitems.checklistIds.length || subitems.questionIds.length
              ? `\n\nChild items will also be deleted (${arrayJoin(
                  [
                    pluralise(
                      subitems.checklistIds.length,
                      '1 checklist',
                      '{x} checklists',
                      '',
                    ),
                    pluralise(
                      subitems.questionIds.length,
                      '1 question',
                      '{x} questions',
                      '',
                    ),
                  ].filter(filterArrayEmpty),
                  ', ',
                  ' and ',
                )})`
              : ''
          }` +
          `${
            !!subitems.actionIds.length || !!subitems.controlIds.length
              ? `\n\nThe ${[
                  pluralise(subitems.actionIds.length, 'action', 'actions', ''),
                  pluralise(
                    subitems.controlIds.length,
                    'control',
                    'controls',
                    '',
                  ),
                ]
                  .filter(filterArrayEmpty)
                  .join(' and ')} will be unlinked but not deleted; ${pluralise(
                  subitems.actionIds.length + subitems.actionIds.length,
                  "it'll",
                  "they'll",
                )} remain available in the suggestions library`
              : ''
          }`,
        confirmText: 'Delete',
        destructive: true,
      }))
    ) {
      return;
    }
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPAssessmentSetupAnswerDelete['response']
      >(http.API_ASESETUP_ANSWER(organisationId, id, true));
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
          suggestions: response.suggestions,
        }),
      );
      yield* put(
        REMOTE_ASESETUP_REMOVE({
          library: {
            questions: [...subitems.questionIds, ...subitems.checklistIds],
            answers: [id, ...subitems.answerIds],
            checklistItems: subitems.checklistItemIds,
          },
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_ACTIONOPEN, function* f(action) {
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.ASESETUPLIBRARYACTION,
        params: {
          incomingValueNewIdKey: !action.payload.id
            ? action.payload.incomingValueNewIdKey
            : undefined,
          allowLinkManagement: false,
        },
      }),
    );
  }),

  takeEvery(REMOTE_ASESETUP_FOLDEROPEN, function* f(action) {
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.ASESETUPLIBRARYFOLDER,
        params: {
          id: action.payload.id,
        },
      }),
    );
  }),

  takeEvery(REMOTE_ASESETUP_SECTIONOPEN, function* f(action) {
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.ASESETUPLIBRARYSECTION,
        params: {
          folderId: action.payload.folderId,
        },
      }),
    );
  }),

  takeEvery(REMOTE_ASESETUP_CONTROLOPEN, function* f(action) {
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.ASESETUPLIBRARYCONTROL,
        params: {
          incomingValueNewIdKey: !action.payload.id
            ? action.payload.incomingValueNewIdKey
            : undefined,
          allowLinkManagement: false,
        },
      }),
    );
  }),

  takeEvery(REMOTE_ASESETUP_FOLDERSAVE, function* f(action) {
    const { id, data } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    // let folderId: number;
    try {
      let response:
        | EPAssessmentSetupFolderCreate['response']
        | EPAssessmentSetupFolderUpdate['response'];
      if (!id) {
        // Create
        response = yield* apiPost<
          EPAssessmentSetupFolderCreate['response'],
          EPAssessmentSetupFolderCreate['post']
        >(http.API_ASESETUP_FOLDER(organisationId), { name: data.name });
      } else {
        // Update
        response = yield* apiPut<
          EPAssessmentSetupFolderUpdate['response'],
          EPAssessmentSetupFolderUpdate['post']
        >(http.API_ASESETUP_FOLDER(organisationId, id), { name: data.name });
      }
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_SECTIONSAVE, function* f(action) {
    const { id, data, addData } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    let sectionId: number;
    try {
      let response:
        | EPAssessmentSetupSectionCreate['response']
        | EPAssessmentSetupSectionUpdate['response'];
      if (!id) {
        if (!addData.folderId) {
          throw new Error('Missing folder ID');
        }
        // Create
        response = yield* apiPost<
          EPAssessmentSetupSectionCreate['response'],
          EPAssessmentSetupSectionCreate['post']
        >(http.API_ASESETUP_SECTION(organisationId), {
          name: data.name,
          description: data.description,
          reportDescription: data.reportDescription,
          folderId: addData.folderId,
        });
        sectionId = Object.values(response.library.sections)[0]!.id;
      } else {
        // Update
        response = yield* apiPut<
          EPAssessmentSetupSectionUpdate['response'],
          EPAssessmentSetupSectionUpdate['post']
        >(http.API_ASESETUP_SECTION(organisationId, id), {
          name: data.name,
          description: data.description,
          reportDescription: data.reportDescription,
        });
        sectionId = id;
      }
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
          opened: {
            sectionId,
          },
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_ACTIONSAVE, function* f(action) {
    const { id, data, incomingValueNewIdKey } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    let actionId: number;
    try {
      // New
      let response:
        | EPAssessmentSetupActionCreate['response']
        | EPAssessmentSetupActionUpdate['response'];
      if (!id) {
        // Create
        response = yield* apiPost<
          EPAssessmentSetupActionCreate['response'],
          EPAssessmentSetupActionCreate['post']
        >(http.API_ASESETUP_ACTION(organisationId), {
          what: data.what,
          why: data.why,
          categoryId: data.categoryId,
          severityId: data.severityId,
          timescaleId: data.timescaleId,
          actionDefinitionId: data.actionDefinitionId || null,
          due: data.due,
          cost: data.cost,
        });
        actionId = parseInt(Object.keys(response.suggestions.actions)[0]!, 10);
      } else {
        // Update
        response = yield* apiPut<
          EPAssessmentSetupActionUpdate['response'],
          EPAssessmentSetupActionUpdate['post']
        >(http.API_ASESETUP_ACTION(organisationId, id), {
          what: data.what,
          why: data.why,
          categoryId: data.categoryId,
          severityId: data.severityId,
          timescaleId: data.timescaleId,
          actionDefinitionId: data.actionDefinitionId || null,
          due: data.due,
          cost: data.cost,
        });
        actionId = id;
      }
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
          opened: {
            actionId,
          },
        }),
      );
      if (incomingValueNewIdKey) {
        yield* put(
          SET_INCOMING_VALUE({ key: incomingValueNewIdKey, value: actionId }),
        );
      }
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_CONTROLSAVE, function* f(action) {
    const { id, data, incomingValueNewIdKey } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    let controlId: number;
    try {
      // New
      let response:
        | EPAssessmentSetupControlCreate['response']
        | EPAssessmentSetupControlUpdate['response'];
      if (!id) {
        // Create
        response = yield* apiPost<
          EPAssessmentSetupControlCreate['response'],
          EPAssessmentSetupControlCreate['post']
        >(http.API_ASESETUP_CONTROL(organisationId), {
          what: data.what,
          why: data.why,
          categoryId: data.categoryId,
        });
        controlId = parseInt(
          Object.keys(response.suggestions.controls)[0]!,
          10,
        );
      } else {
        // Update
        response = yield* apiPut<
          EPAssessmentSetupControlUpdate['response'],
          EPAssessmentSetupControlUpdate['post']
        >(http.API_ASESETUP_CONTROL(organisationId, id), {
          what: data.what,
          why: data.why,
          categoryId: data.categoryId,
        });
        controlId = id;
      }
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
          opened: {
            controlId,
          },
        }),
      );
      if (incomingValueNewIdKey) {
        yield* put(
          SET_INCOMING_VALUE({ key: incomingValueNewIdKey, value: controlId }),
        );
      }
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_ACTIONDELETE, function* f(action) {
    const { id } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      (yield* call(
        apiPost,
        http.API_ASESETUP_ACTION(organisationId, id, true),
      )) as EPAssessmentSetupActionDelete['response'];
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        REMOTE_ASESETUP_REMOVE({
          suggestions: {
            actions: [id],
          },
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_CONTROLDELETE, function* f(action) {
    const { id } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      (yield* call(
        apiPost,
        http.API_ASESETUP_CONTROL(organisationId, id, true),
      )) as EPAssessmentSetupControlDelete['response'];
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(
        REMOTE_ASESETUP_REMOVE({
          suggestions: {
            controls: [id],
          },
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_OPENFOLDERSORDER, function* f() {
    const folderIds = yield* select(
      selectAseSetupLibraryOrganisationHasFolders,
    );
    const folders = yield* select(selectAseSetupLibraryFolders);
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.REORDER,
        params: {
          title: 'Reorder Folders',
          options: folderIds.map((folderId) => ({
            name: folders[folderId]!.name,
            value: folders[folderId]!.id,
          })),
          action: REMOTE_ASESETUP_FOLDERSORDER({ order: [] }),
        },
      }),
    );
  }),

  takeEvery(REMOTE_ASESETUP_FOLDERSORDER, function* f(action) {
    const { order } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPut<
        EPAssessmentSetupFoldersOrder['response'],
        EPAssessmentSetupFoldersOrder['post']
      >(http.API_ASESETUP_FOLDERSORDER(organisationId), {
        order,
      });
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_OPENSECTIONSORDER, function* f(action) {
    const { folderId } = action.payload;
    const folder = yield* select(selectAseSetupLibraryFolder, folderId);
    const sectionIds = yield* select(
      selectAseSetupLibraryFolderHasSections,
      folderId,
    );
    const sections = yield* select(selectAseSetupLibrarySections);
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.REORDER,
        params: {
          title: folder?.name,
          subtitle: 'Reorder Sections',
          options: sectionIds.map((sectionId) => ({
            name: sections[sectionId]!.name,
            value: sections[sectionId]!.id,
          })),
          action: REMOTE_ASESETUP_SECTIONSORDER({ folderId, order: [] }),
        },
      }),
    );
  }),

  takeEvery(REMOTE_ASESETUP_SECTIONSORDER, function* f(action) {
    const { folderId, order } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPut<
        EPAssessmentSetupSectionsOrder['response'],
        EPAssessmentSetupSectionsOrder['post']
      >(http.API_ASESETUP_SECTIONSORDER(organisationId), {
        folderId,
        order,
      });
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(REMOTE_ASESETUP_OPENQUESTIONSORDER, function* f(action) {
    const { sectionId, answerId } = action.payload;
    const parentName = sectionId
      ? (yield* select(selectAseSetupLibrarySection, sectionId))?.name
      : (yield* select(selectAseSetupLibraryAnswer, answerId!))?.answer;
    const questionIds = yield* sectionId
      ? select(selectAseSetupLibrarySectionHasQuestions, sectionId)
      : select(selectAseSetupLibraryAnswerHasQuestions, answerId!);
    const questions = yield* select(selectAseSetupLibraryQuestions);
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.REORDER,
        params: {
          title: parentName,
          subtitle: 'Reorder Checklists & Questions',
          options: questionIds.map((questionId) => {
            const question = questions[questionId]!;
            return {
              name: question.question,
              value: question.id,
              icon:
                question.type === AssessmentQuestionType.checklist
                  ? ICONS.CircleCSolid
                  : ICONS.CircleQSolid,
              iconColor:
                question.type === AssessmentQuestionType.checklist
                  ? theme.colors.checklist
                  : theme.colors.question,
            };
          }),
          action: REMOTE_ASESETUP_QUESTIONSORDER({
            sectionId: sectionId!,
            answerId: answerId as undefined,
            order: [],
          }),
        },
      }),
    );
  }),

  takeEvery(REMOTE_ASESETUP_QUESTIONSORDER, function* f(action) {
    const { sectionId, answerId, order } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId)) || '-1';
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPut<
        EPAssessmentSetupQuestionsOrder['response'],
        EPAssessmentSetupQuestionsOrder['post']
      >(http.API_ASESETUP_QUESTIONSORDER(organisationId), {
        sectionId: sectionId!,
        answerId: answerId as undefined,
        order,
      });
      yield put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK());
      yield* put(
        REMOTE_ASESETUP_MERGE({
          ...response,
        }),
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  /**
   * Billing Admin
   */

  takeEvery(doSetupBillingAdminOpen, function* f(action) {
    const { organisationId } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiGet<EPBillingData['response']>(
        http.apiSettingsBillingNode(organisationId),
      );
      yield* put(doRemoteSetOrganisation(organisationId));
      yield* put(
        doSetupBillingAdminSetData({
          addToUsage: true,
          organisation: data.organisation,
          products: data.products,
          productUnits: data.productUnits,
          items: data.items,
          discounts: data.discounts,
          customers: data.customers,
        }),
      );
      navigateScreenAware(
        Stacks.SETTINGS,
        Screens.SettingsBillingAdmin,
        undefined,
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSetupBillingAdminUpdateProduct, function* f(action) {
    const {
      id,
      name,
      cost,
      months,
      vat,
      account,
      invoiceCode,
      invoiceToOrganisationId,
    } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      let data;
      if (id) {
        data = yield* apiPut<
          EPBillingProductCreate['response'],
          EPBillingProductCreate['post']
        >(http.apiSettingsBillingProduct(organisationId, id), {
          name,
          cost,
          months,
          vat,
          account,
          invoiceCode,
          invoiceToOrganisationId,
        });
      } else {
        data = yield* apiPost<
          EPBillingProductUpdate['response'],
          EPBillingProductUpdate['post']
        >(http.apiSettingsBillingProduct(organisationId), {
          name,
          cost,
          months,
          vat,
          account,
          invoiceCode,
          invoiceToOrganisationId,
        });
      }
      yield* put(
        doSetupBillingAdminSetData({
          addToUsage: false,
          products: data.products,
          productUnits: data.productUnits,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingProduct]));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSetupBillingAdminDeleteProduct, function* f(action) {
    const { id } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    const product = (yield* select(selectBillingProduct, id))!;
    const bl = randomKey();
    try {
      const confirmed = yield* sagaConfirm({
        title: 'Delete Product',
        subtitle: product.name,
        text: 'Deleting this product will delete its units too',
        confirmText: 'Delete Product',
        destructive: true,
      });
      yield* put(SHOW_BLOCKING_LOADER(bl));
      if (confirmed) {
        const data = yield* apiPost<EPBillingProductDelete['response']>(
          http.apiSettingsBillingProduct(organisationId, id, true),
        );
        yield* put(
          doSetupBillingAdminSetData({
            addToUsage: false,
            products: data.products,
            productUnits: data.productUnits,
          }),
        );
      }
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER(bl));
  }),

  takeEvery(doSetupBillingAdminUpdateProductUnits, function* f(action) {
    const { productId, units } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiPost<
        EPBillingProductUnitsUpdate['response'],
        EPBillingProductUnitsUpdate['post']
      >(http.apiSettingsBillingProductUnits(organisationId, productId), {
        units,
      });
      yield* put(
        doSetupBillingAdminSetData({
          addToUsage: false,
          products: data.products,
          productUnits: data.productUnits,
        }),
      );
      yield* put(
        NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingProductUnits]),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSetupBillingAdminDeleteProductUnits, function* f(action) {
    const { productId } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    const product = (yield* select(selectBillingProduct, productId))!;
    const bl = randomKey();
    try {
      const confirmed = yield* sagaConfirm({
        title: 'Delete Product Units',
        subtitle: product.name,
        text: 'Units will be set to 0 for this product',
        confirmText: 'Delete Units',
        destructive: true,
      });
      yield* put(SHOW_BLOCKING_LOADER(bl));
      if (confirmed) {
        const data = yield* apiPost<
          EPBillingProductUnitsUpdate['response'],
          EPBillingProductUnitsUpdate['post']
        >(http.apiSettingsBillingProductUnits(organisationId, productId), {
          units: 0,
        });
        yield* put(
          doSetupBillingAdminSetData({
            addToUsage: false,
            products: data.products,
            productUnits: data.productUnits,
          }),
        );
        yield* put(
          NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingProductUnits]),
        );
      }
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER(bl));
  }),

  takeEvery(doSetupBillingAdminUpdateItem, function* f(action) {
    const {
      id,
      name,
      cost,
      months,
      repeating,
      vat,
      account,
      invoiceCode,
      invoiceToOrganisationId,
    } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      let data;
      if (id) {
        data = yield* apiPut<
          EPBillingItemCreate['response'],
          EPBillingItemCreate['post']
        >(http.apiSettingsBillingItem(organisationId, id), {
          name,
          cost,
          months,
          repeating,
          vat,
          account,
          invoiceCode,
          invoiceToOrganisationId,
        });
      } else {
        data = yield* apiPost<
          EPBillingItemUpdate['response'],
          EPBillingItemUpdate['post']
        >(http.apiSettingsBillingItem(organisationId), {
          name,
          cost,
          months,
          repeating,
          vat,
          account,
          invoiceCode,
          invoiceToOrganisationId,
        });
      }
      yield* put(
        doSetupBillingAdminSetData({
          addToUsage: false,
          items: data.items,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingItem]));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSetupBillingAdminDeleteItem, function* f(action) {
    const { id } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    const billingItem = (yield* select(selectBillingItem, id))!;
    const bl = randomKey();
    try {
      const confirmed = yield* sagaConfirm({
        title: 'Delete Item',
        subtitle: billingItem.name,
        text: 'Deleting this item will remove it from the draft invoice',
        confirmText: 'Delete Item',
        destructive: true,
      });
      yield* put(SHOW_BLOCKING_LOADER(bl));
      if (confirmed) {
        const data = yield* apiPost<EPBillingItemDelete['response']>(
          http.apiSettingsBillingItem(organisationId, id, true),
        );
        yield* put(
          doSetupBillingAdminSetData({
            addToUsage: false,
            items: data.items,
          }),
        );
      }
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER(bl));
  }),

  takeEvery(doSetupBillingAdminUpdateDiscount, function* f(action) {
    const {
      id,
      name,
      expiryDate,
      invoiceCodes,
      type,
      cost,
      remainingCost,
      percent,
    } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      let data;
      if (!id) {
        data = yield* apiPost<
          EPBillingDiscountCreate['response'],
          EPBillingDiscountCreate['post']
        >(http.apiSettingsBillingDiscount(organisationId), {
          name,
          expiryDate,
          invoiceCodes,
          type,
          cost,
          remainingCost,
          percent,
        });
      } else {
        data = yield* apiPut<
          EPBillingDiscountUpdate['response'],
          EPBillingDiscountUpdate['post']
        >(http.apiSettingsBillingDiscount(organisationId, id), {
          name,
          expiryDate,
          invoiceCodes,
        });
      }
      yield* put(
        doSetupBillingAdminSetData({
          addToUsage: false,
          discounts: data.discounts,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingDiscount]));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSetupBillingAdminDeleteDiscount, function* f(action) {
    const { id } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    const billingDiscount = (yield* select(selectBillingDiscount, id))!;
    const bl = randomKey();
    try {
      const confirmed = yield* sagaConfirm({
        title: 'Delete Discount',
        subtitle: billingDiscount.name,
        text: smartText("Are you sure you'd like to delete this discount?"),
        confirmText: 'Delete Discount',
        destructive: true,
      });
      yield* put(SHOW_BLOCKING_LOADER(bl));
      if (confirmed) {
        const data = yield* apiPost<EPBillingDiscountDelete['response']>(
          http.apiSettingsBillingDiscount(organisationId, id, true),
        );
        yield* put(
          doSetupBillingAdminSetData({
            addToUsage: false,
            discounts: data.discounts,
          }),
        );
      }
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER(bl));
  }),

  // Billing
  takeEvery(doSetupBillingOpen, function* f(action) {
    const { organisationId } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiGet<EPBillingGet['response']>(
        http.apiSettingsBillingPhp(organisationId),
      );
      yield* put(doRemoteSetOrganisation(organisationId));
      yield* put(
        doSetupBillingSetData({
          addToUsage: true,
          details: data.details,
          draftInvoices: data.draftInvoices,
          invoices: data.invoices,
          statement: data.statement,
        }),
      );
      navigateScreenAware(Stacks.SETTINGS, Screens.SETTINGS_BILLING, undefined);
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSetupBillingPdf, function* f(action) {
    const { organisationId, reference: invoiceId, doShare } = action.payload;
    if (doShare) {
      yield* put(
        doFileShare({
          url: http.apiSetupBillingInvoicePdf(organisationId, invoiceId),
        }),
      );
    } else {
      yield* put(
        doFilePreviewPdf({
          url: http.apiSetupBillingInvoicePdf(organisationId, invoiceId),
        }),
      );
    }
  }),

  takeEvery(doSetupBillingCreditNote, function* f(action) {
    const { organisationId, reference, doShare } = action.payload;
    yield* put(
      doShare
        ? doFileShare({
            url: http.apiSetupBillingCreditNotePdf(organisationId, reference),
          })
        : doFilePreviewPdf({
            url: http.apiSetupBillingCreditNotePdf(organisationId, reference),
          }),
    );
  }),

  takeEvery(doSetupBillingStatementPdf, function* f(action) {
    const { organisationId, doShare } = action.payload;
    if (doShare) {
      yield* put(
        doFileShare({
          url: http.apiSetupBillingStatementPdf(organisationId),
        }),
      );
    } else {
      yield* put(
        doFilePreviewPdf({
          url: http.apiSetupBillingStatementPdf(organisationId),
        }),
      );
    }
  }),

  takeEvery(doSetupBillingStatementCsv, function* f(action) {
    const { organisationId } = action.payload;
    yield* put(
      doFileShare({
        url: http.apiSetupBillingStatementCsv(organisationId),
      }),
    );
  }),

  takeEvery(doSetupBillingCsv, function* f(action) {
    const { organisationId, reference: invoiceId } = action.payload;
    yield* put(
      doFileShare({
        url: http.apiSetupBillingInvoiceCsv(organisationId, invoiceId),
      }),
    );
  }),

  takeEvery(doSetupBillingDraftPdf, function* f(action) {
    const { organisationId, code, date, doShare } = action.payload;
    if (doShare) {
      yield* put(
        doFileShare({
          url: http.apiSetupBillingDraftPdf(organisationId, code, date),
        }),
      );
    } else {
      yield* put(
        doFilePreviewPdf({
          url: http.apiSetupBillingDraftPdf(organisationId, code, date),
        }),
      );
    }
  }),

  takeEvery(doSetupBillingDraftCsv, function* f(action) {
    const { organisationId, code, date } = action.payload;
    yield* put(
      doFileShare({
        url: http.apiSetupBillingDraftCsv(organisationId, code, date),
      }),
    );
  }),

  /**
   * Reporting
   */

  takeEvery(doReportingLoad, function* f() {
    // const loadStatus = yield* select(selectRptLoadStatus);
    // if (loadStatus === 'loading') {
    //   return;
    // }
    // yield* put(doReportingSetLoadingStatus('loading'));
    // yield* put(SHOW_BLOCKING_LOADER());
    // try {
    //   const response = yield* apiGet<EPReportsGetPages['response']>(
    //     http.apiPagesLoad(),
    //   );
    //   yield* put(
    //     doReportingSetPages({
    //       pages: response.pages.map((page) => ({
    //         ...page,
    //         // Pages don't come with keys – only IDs
    //         key: page.key || page.id,
    //         closeable: page.closeable ?? true,
    //         current: page.current ?? false,
    //         loaded: false,
    //       })),
    //     }),
    //   );
    //   yield* put(doReportingSetLoadingStatus('loaded'));
    // } catch (error) {
    //   yield* put(doReportingSetLoadingStatus('error'));
    //   if (isError(error)) {
    //     yield* put(ALERT_ERROR({ error }));
    //   }
    // }
    // yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doReportingSelectReportOpen, function* f() {
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPReportsGetReports['response'],
        EPReportsGetReports['post']
      >(http.apiReportReports(), {});
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.ReportsSelect,
          params: {
            pages: response.data,
          },
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doReportingSelectReport, function* f(action) {
    yield* put(SHOW_BLOCKING_LOADER());
    const { id, filter, key, loadData } = action.payload;
    try {
      const response = yield* apiPost<
        EPReportsGetReport['response'],
        EPReportsGetReport['post']
      >(http.apiReportReport(id), {
        filter,
        includeVisualData: loadData,
        includeListData: loadData,
      });
      // Close select modal, this may have been opened by a report filter to select an organisation
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
      const finalKey = key || randomKey();
      yield* put(
        doReportingSetReport({
          key: finalKey,
          id: response.id,
          name: response.name,
          description: response.description,
          menuDescription: response.menuDescription,
          icon: response.icon,
          type: response.type,
          typeName: response.typeName,
          filterOptions: response.filterOptions,
          filterValues: response.filterValues,
          typeIsDefault: response.typeIsDefault,
        }),
      );
      if (response.visualData) {
        yield* put(
          doReportingSetDataVisualRaw({
            key: finalKey,
            response: response.visualData,
          }),
        );
      }
      if (response.listData) {
        // yield* put(
        //   doReportingSetFilterValues({
        //     key: finalKey,
        //     filterValues: response.filterValues,
        //   }),
        // );
        yield* put(
          doReportingMergeDataListRaw({
            key: finalKey,
            merge: false,
            pageNumber: 1,
            response: response.listData,
          }),
        );
      }
      if (!loadData) {
        yield* put(
          doReportingShowReportFilterModal({
            key: finalKey,
          }),
        );
      } else {
        yield* put(
          doReportingAddPage({
            id: response.id,
            key: finalKey,
            type: PageTypeEnum.report,
            title: response.name,
            description: response.description,
            menuDescription: response.menuDescription,
            icon: response.icon,
            closeable: true,
            loaded: true,
            config: {
              type: response.type,
            },
          }),
        );
        yield delay(1);
        navigationRef.current?.navigate(Screens.RootPage, {
          screen: Screens.TAB_HOME,
          params: {
            screen: finalKey,
          },
        });
      }
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.ReportsSelect]));
      yield* put(HIDE_BLOCKING_LOADER());
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeEvery(doReportingShowReportFilterModal, function* f(action) {
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.ReportsFilterModal,
        params: {
          reportKey: action.payload.key,
        },
      }),
    );
  }),

  takeEvery(doReportingMergeDraftFilterValues, function* f(action) {
    if (action.payload.loadData) {
      yield* put(
        doReportingGetReportListData({
          key: action.payload.key,
          nextPage: false,
          closeModals: !!action.payload.closeModals,
          overlayLoader: !!action.payload.overlayLoader,
        }),
      );
    }
  }),

  takeEvery(doReportingSetReportVisualsSize, function* f(action) {
    yield* put(
      SET_GENERIC({
        key: genericReportingVisualsSize,
        temp: false,
        value: action.payload.size,
      }),
    );
  }),

  takeEvery(doReportingGetReportListData, function* f(action) {
    const {
      nextPage: incomingNextPage,
      key,
      closeModals,
      overlayLoader,
    } = action.payload;
    const page = (yield* select(selectRptReportPages)).find(
      (p) => key === p.key,
    );
    const nextPage = incomingNextPage && !!page;
    const type = (yield* select(selectRptReportType, key))!;
    // const key = (yield* select(selectRptReportKey))!;
    const pageNumber = (yield* select(selectRptReportPageNumber, key))!;
    const bl = randomKey();
    if (overlayLoader) {
      yield* put(SHOW_BLOCKING_LOADER(bl));
    }
    try {
      if (!overlayLoader) {
        yield* put(doReportingSetDataListLoading({ key, loading: true }));
      }
      const filterValues = (
        !nextPage
          ? yield* select(selectRptReportDraftFilterValues, key)
          : yield* select(selectRptReportFilterValues, key)
      )!;
      const nextPageNumber = nextPage ? pageNumber + 1 : 1;
      const response = yield* apiPost<EPReportsGetReportListData['response']>(
        http.apiReportDataList(),
        {
          type,
          filter: filterValues,
          page: nextPageNumber,
        },
        {
          abortKey: `reporting-${key}`,
        },
      );
      if (!nextPage) {
        yield* put(doReportingSetFilterValues({ key, filterValues }));
      }
      yield* put(
        doReportingMergeDataListRaw({
          key,
          merge: nextPage,
          pageNumber: nextPageNumber,
          response,
        }),
      );
      if (response.visualData !== undefined) {
        yield* put(
          doReportingSetDataVisualRaw({
            key,
            response: response.visualData || { visuals: [] },
          }),
        );
      }
      if (closeModals) {
        yield* put(NAVIGATE_MODAL_BACK([ModalScreens.ReportsFilterModal]));
        yield delay(1);
        yield* put(NAVIGATE_MODAL_BACK([ModalScreens.ReportsSelect]));
      }
      if (!overlayLoader) {
        yield* put(doReportingSetDataListLoading({ key, loading: false }));
      }
      if (!page) {
        const basics = (yield* select(selectRptReportBasics, key))!;
        if (basics) {
          yield* put(
            doReportingAddPage({
              id: basics.id,
              key,
              type: PageTypeEnum.report,
              title: basics.name,
              description: basics.description,
              menuDescription: basics.menuDescription,
              icon: basics.icon,
              closeable: true,
              loaded: true,
              config: {
                type: basics.type,
              },
            }),
          );
          yield delay(1);
          navigationRef.current?.navigate(Screens.RootPage, {
            screen: Screens.TAB_HOME,
            params: {
              screen: key,
            },
          });
        }
      }
    } catch (error) {
      yield* put(doReportingSetDataListLoading({ key, loading: false }));
      if (isError(error) && (!isNetworkError(error) || !error.isCancelled)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    if (overlayLoader) {
      yield* put(HIDE_BLOCKING_LOADER(bl));
    }
  }),

  takeEvery(doReportingGetReportCsv, function* f(action) {
    const { key } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    const type = (yield* select(selectRptReportType, key))!;
    try {
      const filterValues = (yield* select(selectRptReportFilterValues, key))!;
      yield* put(
        doFileShare({
          url: http.apiReportDataCsv(),
          method: 'POST',
          post: {
            type,
            filter: filterValues,
          },
        }),
      );
      yield* put(HIDE_BLOCKING_LOADER());
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  /**
   * Engage
   */

  takeEvery(doSetupEngageOpen, function* f(action) {
    const { organisationId } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiGet<EPEngageGetData['response']>(
        http.apiSettingsEngageData(organisationId),
      );
      yield* put(doRemoteSetOrganisation(organisationId));
      yield* put(
        doSetupEngageSetData({
          addToUsage: true,
          engage: data.engage,
        }),
      );
      navigateScreenAware(Stacks.SETTINGS, Screens.SettingsEngage, undefined);
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSetupEngageUpdateGeneral, function* f(action) {
    const { data: postData } = action.payload;
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiPost<
        EPEngageUpdateGeneral['response'],
        EPEngageUpdateGeneral['post']
      >(http.apiSettingsEngageData(organisationId), postData);
      yield* put(
        doSetupEngageSetData({
          addToUsage: false,
          engage: data.engage,
        }),
      );
      yield* put(
        NAVIGATE_MODAL_BACK([
          ModalScreens.SettingsEngageReportIssue,
          ModalScreens.SettingsEngageMessaging,
        ]),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSetupEngageAddDocuments, function* f(action) {
    const documents = yield* select(selectEngageDocuments);
    const finalElements = orderBy(
      (documents || []).filter((e) => !e.enableAsset),
      'defaultOrdering',
    );
    const eventKey = randomKey();
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        params: {
          title: 'Document Types',
          options: finalElements.map((element) => ({
            name: element.name,
            internalValue: element.id,
          })),
          eventKey,
          enableSelectAll: true,
          multiSelect: true,
          closeOnDone: false,
        },
      }),
    );
    let runAgain: boolean = true;
    do {
      const yielded = yield* race({
        cancel: take(eventSelectScreenClose),
        select: take(eventSelectScreenSelection),
      });
      if (
        (yielded.cancel && yielded.cancel?.payload.key !== eventKey) ||
        (yielded.select && yielded.select?.payload.key !== eventKey)
      ) {
        // wrong eventKey - run 'do' again
      } else if (yielded.cancel) {
        runAgain = false;
      } else if (!yielded.select?.payload.stringValues.length) {
        runAgain = false;
        yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
      } else {
        const organisationId = (yield* select(selectRemoteOrganisationId))!;
        yield* put(SHOW_BLOCKING_LOADER());
        try {
          const data = yield* apiPost<
            EPEngageAddDocuments['response'],
            EPEngageAddDocuments['post']
          >(http.apiSettingsEngageAddDocuments(organisationId), {
            documentIds: yielded.select.payload.stringValues,
          });
          yield* put(
            doSetupEngageSetData({
              addToUsage: false,
              engage: data.engage,
            }),
          );
          yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
          runAgain = false;
        } catch (error) {
          if (isError(error)) {
            yield* put(ALERT_ERROR({ action, error }));
          }
        }
        yield* put(HIDE_BLOCKING_LOADER());
      }
    } while (runAgain);
  }),
  takeEvery(doSetupEngageOrderDocuments, function* f(action) {
    const documents = yield* select(selectEngageDocuments);
    const finalDocuments = orderBy(
      (documents || []).filter((e) => e.enableAsset),
      'ordering',
    );
    const eventKey = randomKey();
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.REORDER,
        params: {
          title: 'Order Documents',
          options: finalDocuments.map((document) => ({
            name: document.name,
            value: document.id,
          })),
          eventKey,
          closeOnDone: false,
        },
      }),
    );
    let runAgain: boolean = true;
    do {
      const yielded = yield* race({
        cancel: take(eventReorderScreenClose),
        done: take(eventReorderScreenDone),
      });
      if (
        (yielded.cancel && yielded.cancel?.payload.key !== eventKey) ||
        (yielded.done && yielded.done?.payload.key !== eventKey)
      ) {
        // wrong eventKey - run 'do' again
      } else if (yielded.cancel) {
        runAgain = false;
      } else {
        const organisationId = (yield* select(selectRemoteOrganisationId))!;
        yield* put(SHOW_BLOCKING_LOADER());
        try {
          const data = yield* apiPost<
            EPEngageOrderDocuments['response'],
            EPEngageOrderDocuments['post']
          >(http.apiSettingsEngageOrderDocuments(organisationId), {
            documentIds: yielded.done?.payload.stringValues || [],
          });
          yield* put(
            doSetupEngageSetData({
              addToUsage: false,
              engage: data.engage,
            }),
          );
          yield* put(NAVIGATE_MODAL_BACK([ModalScreens.REORDER]));
          runAgain = false;
        } catch (error) {
          if (isError(error)) {
            yield* put(ALERT_ERROR({ action, error }));
          }
        }
        yield* put(HIDE_BLOCKING_LOADER());
      }
    } while (runAgain);
  }),
  takeEvery(doSetupEngageRemoveDocument, function* f(action) {
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    const { documentId } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiPost<EPEngageRemoveDocument['response']>(
        http.apiSettingsEngageRemoveDocument(organisationId, documentId),
      );
      yield* put(
        doSetupEngageSetData({
          addToUsage: false,
          engage: data.engage,
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  takeEvery(doSetupEngageRemoveElement, function* f(action) {
    const organisationId = (yield* select(selectRemoteOrganisationId))!;
    const { elementId } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiPost<EPEngageRemoveElement['response']>(
        http.apiSettingsEngageRemoveElement(organisationId, elementId),
      );
      yield* put(
        doSetupEngageSetData({
          addToUsage: false,
          engage: data.engage,
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSetupEngageAddElements, function* f(action) {
    const elements = yield* select(selectEngageElements);
    const finalElements = orderBy(
      (elements || []).filter(
        (e) => !e.enableAsset && !e.enableListing && !e.enablePage,
      ),
      'defaultOrdering',
    );
    const eventKey = randomKey();
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        params: {
          title: 'Element Types',
          options: finalElements.map((element) => ({
            name: element.name,
            groupName: element.sectionName,
            internalValue: element.id,
          })),
          eventKey,
          enableSelectAll: true,
          multiSelect: true,
          closeOnDone: false,
        },
      }),
    );
    let runAgain: boolean = true;
    do {
      const yielded = yield* race({
        cancel: take(eventSelectScreenClose),
        select: take(eventSelectScreenSelection),
      });
      if (
        (yielded.cancel && yielded.cancel?.payload.key !== eventKey) ||
        (yielded.select && yielded.select?.payload.key !== eventKey)
      ) {
        // wrong eventKey - run 'do' again
      } else if (yielded.cancel) {
        runAgain = false;
      } else if (!yielded.select?.payload.stringValues.length) {
        runAgain = false;
        yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
      } else {
        const organisationId = (yield* select(selectRemoteOrganisationId))!;
        yield* put(SHOW_BLOCKING_LOADER());
        try {
          const data = yield* apiPost<
            EPEngageAddElements['response'],
            EPEngageAddElements['post']
          >(http.apiSettingsEngageAddElements(organisationId), {
            elementIds: yielded.select.payload.stringValues,
          });
          yield* put(
            doSetupEngageSetData({
              addToUsage: false,
              engage: data.engage,
            }),
          );
          yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
          runAgain = false;
        } catch (error) {
          if (isError(error)) {
            yield* put(ALERT_ERROR({ action, error }));
          }
        }
        yield* put(HIDE_BLOCKING_LOADER());
      }
    } while (runAgain);
  }),
  takeEvery(doSetupEngageOrderElements, function* f(action) {
    const elements = yield* select(selectEngageElements);
    const finalElements = orderBy(
      (elements || []).filter(
        (e) => e.enableAsset || e.enableListing || e.enablePage,
      ),
      'ordering',
    );
    const eventKey = randomKey();
    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.REORDER,
        params: {
          title: 'Order Elements',
          options: finalElements.map((element) => ({
            name: element.name,
            value: element.id,
          })),
          eventKey,
          closeOnDone: false,
        },
      }),
    );
    let runAgain: boolean = true;
    do {
      const yielded = yield* race({
        cancel: take(eventReorderScreenClose),
        done: take(eventReorderScreenDone),
      });
      if (
        (yielded.cancel && yielded.cancel?.payload.key !== eventKey) ||
        (yielded.done && yielded.done?.payload.key !== eventKey)
      ) {
        // wrong eventKey - run 'do' again
      } else if (yielded.cancel) {
        runAgain = false;
      } else {
        const organisationId = (yield* select(selectRemoteOrganisationId))!;
        yield* put(SHOW_BLOCKING_LOADER());
        try {
          const data = yield* apiPost<
            EPEngageOrderElements['response'],
            EPEngageOrderElements['post']
          >(http.apiSettingsEngageOrderElements(organisationId), {
            elementIds: yielded.done?.payload.stringValues || [],
          });
          yield* put(
            doSetupEngageSetData({
              addToUsage: false,
              engage: data.engage,
            }),
          );
          yield* put(NAVIGATE_MODAL_BACK([ModalScreens.REORDER]));
          runAgain = false;
        } catch (error) {
          if (isError(error)) {
            yield* put(ALERT_ERROR({ action, error }));
          }
        }
        yield* put(HIDE_BLOCKING_LOADER());
      }
    } while (runAgain);
  }),
  takeEvery(doSetupEngageElementUpdate, function* f(action) {
    const { element } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const organisationId = (yield* select(selectRemoteOrganisationId))!;
      const data = yield* apiPost<EPEngageUpdateElement['response']>(
        http.apiSettingsEngageUpdateElement(organisationId, element.id),
        element,
      );
      yield* put(
        doSetupEngageSetData({
          addToUsage: true,
          engage: data.engage,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsEngageElement]));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSetupEngageDocumentUpdate, function* f(action) {
    const { document } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const organisationId = (yield* select(selectRemoteOrganisationId))!;
      const data = yield* apiPost<EPEngageUpdateDocument['response']>(
        http.apiSettingsEngageUpdateDocument(organisationId, document.id),
        document,
      );
      yield* put(
        doSetupEngageSetData({
          addToUsage: true,
          engage: data.engage,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsEngageDocument]));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  /**
   * API
   */

  takeEvery(doSetupApiOpen, function* f(action) {
    const { organisationId } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiGet<EPApiGetData['response']>(
        http.apiSettingsApiData(organisationId),
      );
      yield* put(doRemoteSetOrganisation(organisationId));
      yield* put(
        doSetupApiSetData({
          addToUsage: true,
          users: data.users,
          webhooks: data.webhooks,
        }),
      );
      navigateScreenAware(Stacks.SETTINGS, Screens.SettingsApi, undefined);
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  // API Users
  takeEvery(doSetupApiCreateUserPreflight, function* f(action) {
    const { organisationId } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiGet<EPApiCreateUserPreflight['response']>(
        http.apiSettingsApiUser(organisationId),
      );
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.SettingsApiUser,
          params: {
            type: 'new',
            data: {
              token: data.token,
              bearerToken: data.bearerToken,
              organisationId,
            },
          },
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  takeEvery(doSetupApiCreateUser, function* f(action) {
    const { organisationId, token, name } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiPost<EPApiCreateUser['response']>(
        http.apiSettingsApiUser(organisationId),
        {
          token,
          name,
        },
      );
      yield* put(
        doSetupApiSetData({
          addToUsage: false,
          users: data.users,
          webhooks: data.webhooks,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsApiUser]));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  takeEvery(doSetupApiUpdateUser, function* f(action) {
    const { organisationId, id, name } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiPost<EPApiUpdateUser['response']>(
        http.apiSettingsApiUser(organisationId, id),
        {
          name,
        },
      );
      yield* put(
        doSetupApiSetData({
          addToUsage: false,
          users: data.users,
          webhooks: data.webhooks,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsApiUser]));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  takeEvery(doSetupApiDeleteUser, function* f(action) {
    const { organisationId, id, name } = action.payload;
    let blockingLoaderShown = false;
    try {
      if (
        yield* sagaConfirm({
          title: 'Delete API User',
          text:
            `Integrations using this user${str.apos}s token will stop working. ` +
            'Only delete a user if its token is no longer being used or ' +
            'if the token has been compromised',
          subtitle: name,
          destructive: true,
          confirmText: 'Delete User',
          delayedConfirm: true,
        })
      ) {
        blockingLoaderShown = true;
        yield* put(SHOW_BLOCKING_LOADER());
        const data = yield* apiPost<EPApiDeleteUser['response']>(
          http.apiSettingsApiUser(organisationId, id, true),
        );
        yield* put(
          doSetupApiSetData({
            addToUsage: false,
            users: data.users,
            webhooks: data.webhooks,
          }),
        );
        yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsApiUser]));
        yield* put(doToast('User Deleted'));
        yield* put(HIDE_BLOCKING_LOADER());
      }
    } catch (error) {
      if (blockingLoaderShown) {
        yield* put(HIDE_BLOCKING_LOADER());
      }
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  // API Webhooks
  takeEvery(doSetupApiCreateWebhookPreflight, function* f(action) {
    const { organisationId } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      yield* apiGet<EPApiCreateWebhookPreflight['response']>(
        http.apiSettingsApiWebhook(organisationId),
      );
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.SettingsApiWebhook,
          params: {
            type: 'new',
            data: {
              organisationId,
            },
          },
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  takeEvery(doSetupApiCreateWebhook, function* f(action) {
    const { organisationId, url, secret } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiPost<EPApiCreateWebhook['response']>(
        http.apiSettingsApiWebhook(organisationId),
        {
          url,
          secret,
        },
      );
      yield* put(
        doSetupApiSetData({
          addToUsage: false,
          users: data.users,
          webhooks: data.webhooks,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsApiWebhook]));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  takeEvery(doSetupApiUpdateWebhook, function* f(action) {
    const { organisationId, id, secret, url } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiPost<EPApiUpdateWebhook['response']>(
        http.apiSettingsApiWebhook(organisationId, id),
        {
          secret,
          url,
        },
      );
      yield* put(
        doSetupApiSetData({
          addToUsage: false,
          users: data.users,
          webhooks: data.webhooks,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsApiWebhook]));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  takeEvery(doSetupApiTestWebhook, function* f(action) {
    const { organisationId, id, secret, url } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      yield* apiPost<EPApiTestWebhook['response']>(
        http.apiSettingsApiTestWebhook(organisationId, id),
        {
          secret,
          url,
        },
      );
      yield* put(
        ALERT({
          icon: ICONS.CircleCheckLight,
          text: 'Webhook succeeded with response code: 200',
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(
          ALERT({ icon: ICONS.CircleXmarkLight, text: error.message }),
        );
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  takeEvery(doSetupApiDeleteWebhook, function* f(action) {
    const { organisationId, id, url } = action.payload;
    let blockingLoaderShown = false;
    try {
      if (
        yield* sagaConfirm({
          title: 'Delete API Webhook',
          text: 'RiskBase will no longer run this webhook',
          subtitle: url,
          destructive: true,
          confirmText: 'Delete Webhook',
          delayedConfirm: true,
        })
      ) {
        blockingLoaderShown = true;
        yield* put(SHOW_BLOCKING_LOADER());
        const data = yield* apiPost<EPApiDeleteWebhook['response']>(
          http.apiSettingsApiWebhook(organisationId, id, true),
        );
        yield* put(
          doSetupApiSetData({
            addToUsage: false,
            users: data.users,
            webhooks: data.webhooks,
          }),
        );
        yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsApiWebhook]));
        yield* put(doToast('User Deleted'));
        yield* put(HIDE_BLOCKING_LOADER());
      }
    } catch (error) {
      if (blockingLoaderShown) {
        yield* put(HIDE_BLOCKING_LOADER());
      }
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  /**
   * SSO
   */

  takeEvery(doSetupSsoOpen, function* f(action) {
    const { organisationId } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiGet<EPSsoSettingsGetData['response']>(
        http.apiSettingsSsoData(organisationId),
      );
      yield* put(doRemoteSetOrganisation(organisationId));
      yield* put(
        doSetupSsoSetData({
          addToUsage: true,
          data,
        }),
      );
      navigateScreenAware(Stacks.SETTINGS, Screens.SettingsSso, undefined);
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSetupSsoUpdate, function* f(action) {
    const {
      organisationId,
      samlEnabled,
      samlIdpCertificate,
      samlIdpEntityId,
      scimEnabled,
    } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const data = yield* apiPost<
        EPSsoSettingsUpdate['response'],
        EPSsoSettingsUpdate['post']
      >(http.apiSettingsSsoUpdate(organisationId), {
        samlEnabled,
        samlIdpCertificate,
        samlIdpEntityId,
        scimEnabled,
      });
      yield* put(
        doSetupSsoSetData({
          addToUsage: true,
          data,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsSsoIdp]));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  // Organisation

  takeEvery(doSetupOrganisationOpen, function* f(action) {
    const { newOrganisationId } = action.payload;
    const finalNewOrganisation =
      newOrganisationId || (yield* select(selectUserSelectedOrganisation))?.id;
    if (!finalNewOrganisation) {
      return;
    }
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiGet<EPOrganisationSettingsData['response']>(
        http.apiSettingsOrganisationData(finalNewOrganisation),
      );
      yield* put(
        doUserOrganisationSet({
          organisation: response.organisation,
        }),
      );
      yield* put(
        doSetupOrganisationSetData({
          addToUsage: true,
          setupOrganisation: response.setupOrganisation,
          billingCountries: response.billingCountries,
          customers: response.customers,
        }),
      );
      yield* put(systemEventSetupOrganisationOpened());

      // Delay required otherwise navigation doesn't work on iOS
      yield delay(1);
      navigateScreenAware(
        Stacks.SETTINGS,
        Screens.SETTINGS_ORGANISATION,
        undefined,
      );
    } catch (error) {
      yield* put(systemEventSetupOrganisationNotOpened());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSetupOrganisationOnBehalfOfOpen, function* f(action) {
    const organisation = yield* select(selectUserSelectedOrganisation);
    const customers = yield* select(selectRemoteCustomers);
    const setupOrganisation = yield* select(selectSetupOrganisation);
    if (!organisation || !setupOrganisation || !customers) {
      return;
    }

    const eventKey = randomKey();

    yield* put(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        params: {
          title: 'On Behalf Of',
          value: (setupOrganisation.onBehalfOf || []).map((c) => c.id),
          eventKey,
          multiSelect: true,
          searchable: true,
          closeOnDone: false,
          options: naturalOrderBy(
            Object.values(customers)
              .filter((c) => c.id !== organisation.id)
              .map((c) => ({
                name: c.name,
                internalValue: c.id,
              })),
            (v) => v.name,
          ),
        },
      }),
    );

    let finish = false;
    do {
      const yielded = yield* race({
        cancel: take(eventSelectScreenClose),
        select: take(eventSelectScreenSelection),
      });
      if (
        (yielded.cancel && yielded.cancel.payload.key !== eventKey) ||
        (yielded.select && yielded.select.payload.key !== eventKey)
      ) {
        // wrong eventKey - run 'do' again
      } else if (yielded.select) {
        yield* put(SHOW_BLOCKING_LOADER());
        try {
          const response = yield* apiPost<
            EPOrganisationSettingsSave['response'],
            EPOrganisationSettingsSave['post']
          >(http.apiSettingsOrganisationData(organisation.id), {
            onBehalfOf: yielded.select.payload.stringValues,
          });
          yield* put(
            doUserOrganisationSet({
              organisation: response.organisation,
            }),
          );
          yield* put(
            doSetupOrganisationUpdateData({
              setupOrganisation: response.setupOrganisation,
            }),
          );
          yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
          finish = true;
        } catch (error) {
          if (isError(error)) {
            yield* put(ALERT_ERROR({ action, error }));
          }
        }
        yield* put(HIDE_BLOCKING_LOADER());
      } else {
        finish = true;
      }
    } while (!finish);
  }),
  takeEvery(doSetupOrganisationSave, function* f(action) {
    const { changes } = action.payload;
    const organisation = yield* select(selectUserSelectedOrganisation);
    const setupOrganisation = yield* select(selectSetupOrganisation);
    if (!organisation || !setupOrganisation) {
      return;
    }
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPOrganisationSettingsSave['response'],
        EPOrganisationSettingsSave['post']
      >(http.apiSettingsOrganisationData(organisation.id), changes);
      yield* put(
        doUserOrganisationSet({
          organisation: response.organisation,
        }),
      );
      yield* put(
        doSetupOrganisationUpdateData({
          setupOrganisation: response.setupOrganisation,
        }),
      );
      yield* put(
        NAVIGATE_MODAL_BACK([
          ModalScreens.SettingsOrganisationDetailsModal,
          ModalScreens.SettingsOrganisationBillingModal,
        ]),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  takeEvery(doSetupBillingAdminOrganisationSave, function* f(action) {
    const { changes } = action.payload;
    const organisation = yield* select(selectUserSelectedOrganisation);
    if (!organisation) {
      return;
    }
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* apiPost<
        EPBillingAdminOrganisationSave['response'],
        EPBillingAdminOrganisationSave['post']
      >(http.apiSettingsBillingNode(organisation.id), changes);
      yield* put(
        doSetupBillingAdminSetData({
          addToUsage: false,
          organisation: response.organisation,
        }),
      );
      yield* put(
        NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingOrganisation]),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSetupOrganisationSaveLogo, function* f(action) {
    const { file } = action.payload;
    const organisation = yield* select(selectUserSelectedOrganisation);
    if (!organisation) {
      return;
    }
    try {
      yield* put(SHOW_BLOCKING_LOADER());
      let response:
        | EPOrganisationSettingsLogoSave['response']
        | EPOrganisationSettingsLogoDelete['response'];
      if (!file) {
        // Remove logo
        response = yield* apiPost<
          EPOrganisationSettingsLogoDelete['response'],
          EPOrganisationSettingsLogoDelete['post']
        >(http.apiSettingsOrganisationLogo(organisation?.id, true));
      } else {
        // Add logo
        const fileNameParts = file.name.split('.');
        const fileType = getFileTypeFromUntrustedExtension(
          last(fileNameParts)?.toLowerCase() || '',
        );
        if (
          !fileType ||
          ![FileTypeGroupsEnum.svg, FileTypeGroupsEnum.image].includes(
            fileType.group,
          )
        ) {
          throw new Error('Logo must be an SVG or an image');
        }
        response = yield* apiPost<
          EPOrganisationSettingsLogoSave['response'],
          EPOrganisationSettingsLogoSave['post']
        >(
          http.apiSettingsOrganisationLogo(organisation?.id),
          yield* call(returnFileAsBase64, { file }),
        );
      }
      yield* put(
        doUserOrganisationSet({
          organisation: response.organisation,
        }),
      );
      yield* put(HIDE_BLOCKING_LOADER());
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  /* Compliance & Assessment Types Setup */
  takeEvery(doSettingsComplianceAndAssessmentTypesLoad, function* f(action) {
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response =
        yield* requestGet<SettingsGetComplianceAndAssessmentTypes>({
          api: http.Api.NODE,
          url: '/organisation-settings/:organisationUid/compliance-types',
          params: { organisationUid: action.payload.organisationId },
        });
      yield* put(doSettingsComplianceAndAssessmentTypesSetData(response));
      yield put(HIDE_BLOCKING_LOADER());
      navigateScreenAware(
        Stacks.SETTINGS,
        Screens.SettingsComplianceAndAssessmentTypes,
        undefined,
      );
    } catch (error) {
      yield put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doCreateComplianceType, function* f(action) {
    const {
      organisationUid,
      resource: { name },
      eventKey,
    } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const { complianceType } = yield* requestPost<SettingsPostComplianceType>(
        {
          api: http.Api.NODE,
          url: '/organisation-settings/:organisationUid/compliance-types',
          params: { organisationUid },
          body: { name },
        },
      );

      yield* put(
        doSettingsComplianceTypeUpdate({
          resource: complianceType,
        }),
      );

      // Delay is required to allow state update before animation runs
      yield delay(250);
      yield* put(
        eventTypeScrollToItem({
          key: eventKey,
          item: `ct-${complianceType.id}`,
        }),
      );
      yield* put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK());
      yield* put(doToast('Compliance Type Created Successfully'));
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doUpdateComplianceType, function* f(action) {
    const {
      organisationUid,
      resource: { name, complianceTypeId },
      eventKey,
    } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const { complianceType } =
        yield* requestPatch<SettingsPatchComplianceType>({
          api: http.Api.NODE,
          url: '/organisation-settings/:organisationUid/compliance-types/:complianceTypeUid',
          params: {
            organisationUid,
            complianceTypeUid: complianceTypeId,
          },
          body: { name },
        });

      yield* put(
        doSettingsComplianceTypeUpdate({
          resource: complianceType,
        }),
      );

      yield* put(HIDE_BLOCKING_LOADER());

      // Delay is required to allow state update before animation runs
      yield delay(250);
      yield* put(
        eventTypeScrollToItem({
          key: eventKey,
          item: `ct-${complianceType.id}`,
        }),
      );

      yield* put(NAVIGATE_MODAL_BACK());
      yield* put(doToast('Compliance Type Updated Successfully'));
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doCreateAssessmentType, function* f(action) {
    const {
      organisationUid,
      resource: { name, complianceTypeId },
      eventKey,
    } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const { assessmentType } = yield* requestPost<SettingsPostAssessmentType>(
        {
          api: http.Api.NODE,
          url: '/organisation-settings/:organisationUid/assessment-types',
          params: { organisationUid },
          body: { name, complianceTypeId },
        },
      );

      yield* put(
        doSettingsAssessmentTypeUpdate({
          resource: assessmentType,
        }),
      );

      // Delay is required to allow state update before animation runs
      yield delay(250);
      yield* put(
        eventTypeScrollToItem({
          key: eventKey,
          item: `at-${assessmentType.id}`,
        }),
      );
      yield* put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK());
      yield* put(doToast('Assessment Type Created Successfully'));
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doUpdateAssessmentType, function* f(action) {
    const {
      organisationUid,
      resource: { name, assessmentTypeId },
      eventKey,
    } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const { assessmentType } =
        yield* requestPatch<SettingsPatchAssessmentType>({
          api: http.Api.NODE,
          url: '/organisation-settings/:organisationUid/assessment-types/:assessmentTypeUid',
          params: { organisationUid, assessmentTypeUid: assessmentTypeId },
          body: { name },
        });

      yield* put(
        doSettingsAssessmentTypeUpdate({
          resource: assessmentType,
        }),
      );

      // Delay is required to allow state update before animation runs
      yield delay(250);
      yield* put(
        eventTypeScrollToItem({
          key: eventKey,
          item: `at-${assessmentType.id}`,
        }),
      );
      yield* put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK());
      yield* put(doToast('Assessment Type Updated Successfully'));
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  /* Severities Setup */
  takeEvery(doSettingsSeveritiesOpen, function* f(action) {
    try {
      navigateScreenAware(
        Stacks.SETTINGS,
        Screens.SettingsSeverities,
        undefined,
      );
      yield* put(
        doSettingsSeveritiesSetData({
          loadStatus: 'loading',
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doSettingsSeveritiesLoad, function* f(action) {
    try {
      const { severities, sharedWithOrganisations, managedByOrganisation } =
        yield* requestRoute({
          api: http.Api.APP,
          route: appApiRoutes.organisationSettings.getSeverities,
          params: { organisationId: action.payload.organisationId },
        });

      yield* put(
        doSettingsSeveritiesSetData({
          loadStatus: 'loaded',
          severities,
        }),
      );

      yield* put(
        doSettingsSeveritiesSharedWithOrganisationsSetData({
          sharedWithOrganisations,
        }),
      );

      yield* put(
        doSettingsSeveritiesManagingOrganisationSetData({
          managingOrganisation: managedByOrganisation ?? undefined,
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }

      yield* put(
        doSettingsSeveritiesSetData({
          loadStatus: 'error',
        }),
      );
    }
  }),

  takeEvery(doSettingsSeveritiesReorder, function* f(action) {
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const { severities } = yield* requestRoute({
        api: http.Api.APP,
        route: appApiRoutes.organisationSettings.reorderSeverities,
        params: {
          organisationId: action.payload.organisationId,
        },
        body: {
          severityIds: action.payload.order,
        },
      });

      yield* put(
        doSettingsSeveritiesSetData({
          loadStatus: 'loaded',
          severities,
        }),
      );

      yield* put(NAVIGATE_MODAL_BACK());
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSettingsSeveritiesUpdate, function* f(action) {
    try {
      yield* requestRoute({
        api: http.Api.APP,
        route: appApiRoutes.organisationSettings.updateSeverity,
        params: {
          organisationId: action.payload.organisationId,
          severityId: action.payload.severity.id,
        },
        body: {
          name: action.payload.severity.name,
          color: action.payload.severity.color,
          description: action.payload.severity.description,
        },
      });

      yield* put(
        doSettingsSeveritiesSet({
          severity: action.payload.severity,
        }),
      );

      yield* put(NAVIGATE_MODAL_BACK());
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doSettingsSeveritiesCreate, function* f(action) {
    try {
      const severity = yield* requestRoute({
        api: http.Api.APP,
        route: appApiRoutes.organisationSettings.createSeverity,
        params: {
          organisationId: action.payload.organisationId,
        },
        body: {
          name: action.payload.severity.name,
          color: action.payload.severity.color,
          description: action.payload.severity.description,
        },
      });

      yield* put(
        doSettingsSeveritiesSet({
          severity,
        }),
      );

      yield* put(NAVIGATE_MODAL_BACK());
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doSettingsSeveritiesDelete, function* f(action) {
    try {
      const confirmed = yield* sagaConfirm({
        title: 'Are you sure you want to delete this task severity?',
        text: 'This cannot be undone',
        confirmText: 'Delete Severity',
        destructive: true,
      });

      if (!confirmed) {
        return;
      }

      yield* requestRoute({
        api: http.Api.APP,
        route: appApiRoutes.organisationSettings.deleteSeverity,
        params: {
          organisationId: action.payload.organisationId,
          severityId: action.payload.severityId,
        },
      });

      yield* put(
        doSettingsSeveritiesRemove({
          severityId: action.payload.severityId,
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),
  /* Integrations Setup */
  /* eslint-disable no-console -- Integration API diagnostics are intentionally retained. */
  takeEvery(doSettingsIntegrationsLoad, function* f(action) {
    yield* put(doSettingsIntegrationsSetData({ loadStatus: 'loading' }));
    try {
      console.log('------ CALLING integrations.list ------ ');
      console.log('PAYLOAD:', action.payload.organisationId);
      const { integrations } = yield* requestRoute({
        api: http.Api.APP,
        route: appApiRoutes.integrations.list,
        params: { organisationId: action.payload.organisationId },
      });
      console.log('RESPONSE:', integrations);

      console.log('------ CALLING integrations.listPartners ------ ');
      console.log('PAYLOAD:', action.payload.organisationId);
      const { integrationPartners } = yield* requestRoute({
        api: http.Api.APP,
        route: appApiRoutes.integrations.listPartners,
        params: { organisationId: action.payload.organisationId },
      });
      console.log('RESPONSE:', integrationPartners);

      yield* put(
        doSettingsIntegrationsSetData({
          loadStatus: 'loaded',
          integrations,
          partners: integrationPartners,
        }),
      );
    } catch (error) {
      yield* put(doSettingsIntegrationsSetData({ loadStatus: 'error' }));
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doSettingsIntegrationCreate, function* f(action) {
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      console.log('------ CALLING integrations.create ------ ');
      console.log(
        'PAYLOAD:',
        'Params: ',
        action.payload.organisationId,
        'Body: ',
        action.payload.integrationPartnerId,
        action.payload.integrationName,
      );
      const integration = yield* requestRoute({
        api: http.Api.APP,
        route: appApiRoutes.integrations.create,
        params: { organisationId: action.payload.organisationId },
        body: {
          integrationPartnerId: action.payload.integrationPartnerId,
          integrationName: action.payload.integrationName,
        },
      });
      console.log('RESPONSE:', integration);
      yield* put(doSettingsIntegrationSet({ integration }));
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsIntegrationAdd]));
      yield delay(1);
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.SettingsIntegrationEdit,
          params: {
            organisationId: action.payload.organisationId,
            integrationId: integration.uid,
          },
        }),
      );
      yield* put(doToast('Integration Added Successfully'));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSettingsIntegrationUpdate, function* f(action) {
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      console.log('------ CALLING integrations.update ------ ');
      console.log(
        'PAYLOAD:',
        'Params: ',
        action.payload.organisationId,
        action.payload.integrationId,
        'Body: ',
        action.payload.update,
      );
      const integration = yield* requestRoute({
        api: http.Api.APP,
        route: appApiRoutes.integrations.update,
        params: {
          organisationId: action.payload.organisationId,
          integrationId: action.payload.integrationId,
        },
        body: action.payload.update,
      });
      console.log('RESPONSE:', integration);
      yield* put(doSettingsIntegrationSet({ integration }));
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SettingsIntegrationEdit]));
      yield* put(doToast('Integration Updated Successfully'));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doSettingsIntegrationRemove, function* f(action) {
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      console.log('------ CALLING integrations.remove ------ ');
      console.log(
        'PAYLOAD:',
        'Params: ',
        action.payload.organisationId,
        action.payload.integrationId,
      );
      yield* requestRoute({
        api: http.Api.APP,
        route: appApiRoutes.integrations.remove,
        params: {
          organisationId: action.payload.organisationId,
          integrationId: action.payload.integrationId,
        },
      });
      yield* put(
        doSettingsIntegrationRemoved({
          integrationId: action.payload.integrationId,
        }),
      );
      yield* put(doToast('Integration Removed Successfully'));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),
  /* eslint-enable no-console */
];
